import "./BibleReader.inline.css";
import "./BibleReader.css";
import {
  type Translation,
  type TranslationBookChapter,
  type ChapterVerse,
} from "../../managers/FreeUseBibleAPI";
import {
  Fragment,
  type ComponentChildren,
  type JSX,
  type RefObject,
} from "preact";
import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useLayoutEffect,
  useState,
} from "preact/compat";
import { computed, type ReadonlySignal, type Signal } from "@preact/signals";
import {
  adjacentInlineRect,
  buildRibbonPath,
  collectLineRects,
  type RibbonRect,
  RIBBON_RADIUS_EM,
  RIBBON_PAD_X_EM,
} from "../../app/highlightRibbon";
import type {
  BibleReadingState,
  BibleSelectedVerse,
  VerseDecoration,
  VisibleVerseRange,
} from "../../managers/BibleReadingManager";
import type {
  ChapterHighlight,
  ChapterHighlights,
} from "../../managers/HighlightsManager";
import type { BibleSelectorState } from "../../managers/BibleSelectorManager";
import type { TabSlot } from "../../managers/TabsLayoutManager";
import type { ScriptureElementsBehavior } from "../../managers/SettingsManager";
import type { SeedBibleState } from "../../managers/SeedBibleStateManager";
import {
  annotationVerseNumbers,
  visibleChapterAnnotations,
  type Annotation,
  type AnnotationsManager,
} from "../../managers/AnnotationsManager";
import type { FriendsManager } from "../../managers/FriendsManager";
import type {
  BibleReadingSession,
  ConnectionSessionUserVisual,
} from "../../managers/SessionsManager";
import { Avatar, getUserDisplayName } from "../Avatar/Avatar";
import { useI18n } from "../../i18n/I18nManager";
import { useOverlayDismiss } from "../useOverlayDismiss";
import { MobileSettingsSheet } from "../../components/MobileSettingsSheet/MobileSettingsSheet";
import { MobileSessionParticipants } from "../../components/SessionParticipants/SessionParticipants";
import { InfoSettingsIcon, MaterialIcon } from "../../components/icons";
import { QuickToolbar } from "../../components/QuickToolbar/QuickToolbar";
import { Skeleton, SkeletonContainer } from "../Skeleton/Skeleton";
import { Spinner } from "../Spinner/Spinner";
import {
  SaveStarIcon,
  openSaveModalForLocation,
  saveChapterLabel,
} from "../Tabs/Tabs";
import {
  BookmarkStackIcon,
  bookmarkButtonLabel,
  openBookmarkModal,
} from "../Bookmarks/Bookmarks";
import { VerseReferenceText } from "../../app/verseReferenceLink";
import { flingSafeTapHandlers } from "../../app/flingSafeTap";
import { DiscoverContentPanel } from "../DiscoverContentPanel/DiscoverContentPanel";
import { findOfflineTranslationFallbacks } from "../../managers/offlineTranslationFallback";
import { SearchableSelect } from "../SearchableSelect/SearchableSelect";
import { urlWithoutEmbedParam } from "../../managers/EmbedMode";
import {
  findScrollContainer,
  measureVisibleReaderBounds,
} from "./readerViewport";

interface ReaderChapterActionProps {
  state: SeedBibleState;
  translationId: string | null;
  bookId: string | null;
  chapterNumber: number | null;
}

/**
 * Files the chapter currently shown in the reader. Sits in the top-right
 * action cluster and opens the same folder picker a verse selection does, so
 * one press archives the whole chapter into an existing or new folder.
 *
 * The star fills once a chapter-level save exists, and pressing a filled star
 * edits that save's folders rather than filing a second copy. Either way it is
 * not a toggle — removing a save is done from the saves panel.
 */
function ReaderSaveButton(props: ReaderChapterActionProps) {
  const { state, translationId, bookId, chapterNumber } = props;
  const { t } = useI18n();
  const canSave = !!(translationId && bookId && chapterNumber);
  const isSaved =
    canSave &&
    state.saves.isLocationSaved(translationId, bookId, chapterNumber);

  return (
    <button
      type="button"
      className={`sb-bible-reader-save-button${
        isSaved ? " sb-bible-reader-save-button-saved" : ""
      }`}
      onClick={() => {
        if (!canSave || !translationId || !bookId || !chapterNumber) {
          return;
        }
        openSaveModalForLocation(state, {
          translationId,
          bookId,
          chapterNumber,
        });
      }}
      disabled={!canSave}
      aria-label={saveChapterLabel(t, isSaved)}
      title={saveChapterLabel(t, isSaved)}
    >
      <SaveStarIcon isSaved={isSaved} />
    </button>
  );
}

/**
 * Offers downloaded translations the reader can switch to after a chapter
 * load fails. One match is a switch button; two or more are chosen from the
 * same searchable picker Settings uses for language. The message above this
 * stays generic. These controls are what name the translations.
 */
function OfflineFallbackSwitch(props: {
  translations: Translation[];
  onSwitch: (translation: Translation) => void;
}) {
  const { translations, onSwitch } = props;
  const { t } = useI18n();
  const [selectedId, setSelectedId] = useState(translations[0]?.id ?? "");
  const selected =
    translations.find((item) => item.id === selectedId) ?? translations[0];
  if (!selected) {
    return null;
  }

  return translations.length === 1 ? (
    <button
      type="button"
      className="sb-reader-error-switch"
      onClick={() => onSwitch(selected)}
    >
      {t("chapter-unavailable-offline-switch-action", {
        defaultValue: "Switch to {{name}}",
        name: selected.name,
      })}
    </button>
  ) : (
    <div className="sb-reader-error-offline-pick">
      <label
        className="sb-reader-error-offline-label"
        htmlFor="sb-reader-error-offline-select"
      >
        {t("chapter-unavailable-offline-switch-choose", {
          defaultValue: "Choose a saved translation",
        })}
      </label>
      <SearchableSelect
        id="sb-reader-error-offline-select"
        value={selected.id}
        options={translations.map((item) => ({
          id: item.id,
          label: item.name,
        }))}
        onChange={setSelectedId}
        searchPlaceholder={t("search", { defaultValue: "Search" })}
        emptyLabel={t("chapter-unavailable-offline-switch-empty", {
          defaultValue: "No matching translations",
        })}
      />
      <button
        type="button"
        className="sb-reader-error-switch"
        onClick={() => onSwitch(selected)}
      >
        {t("chapter-unavailable-offline-switch-confirm", {
          defaultValue: "Switch",
        })}
      </button>
    </div>
  );
}

/**
 * Opens the bookmark modal for the chapter in the reader. The icon shows what
 * is already here: an outline for nothing, the bookmark's own color for one,
 * and a fan of colors for several — only bookmarks in this translation count.
 */
function ReaderBookmarkButton(props: ReaderChapterActionProps) {
  const { state, translationId, bookId, chapterNumber } = props;
  const { t } = useI18n();
  const canBookmark = !!(translationId && bookId && chapterNumber);
  const bookmarksHere = state.bookmarks.getBookmarksForLocation(
    translationId,
    bookId,
    chapterNumber
  );
  const label = bookmarkButtonLabel(t, bookmarksHere);

  return (
    <button
      type="button"
      className={`sb-bible-reader-bookmark-button${
        bookmarksHere.length > 0 ? " sb-bible-reader-bookmark-button-set" : ""
      }`}
      onClick={() => {
        if (!translationId || !bookId || !chapterNumber) {
          return;
        }
        void openBookmarkModal(state, { translationId, bookId, chapterNumber });
      }}
      disabled={!canBookmark}
      aria-label={label}
      title={label}
    >
      <BookmarkStackIcon bookmarks={bookmarksHere} />
    </button>
  );
}

interface ChapterNotesButtonProps {
  state: SeedBibleState;
  bookId: string | null;
  chapterNumber: number | null;
}

/**
 * Shows the note count for the chapter currently in view; hidden entirely
 * when the chapter has no annotations. Jumps to (scrolls to and highlights)
 * the earliest annotated verse's group in the compact discover panel, which
 * is always visible inline below the scripture text on mobile — see
 * `AnnotationsSection`'s `scrollToVerse` consumer. Falls back to opening the
 * full Discover pane when none of the chapter's annotations target a
 * specific verse (whole-chapter annotations only), since there's nothing for
 * the compact panel to scroll to in that case.
 */
function ChapterNotesButton(props: ChapterNotesButtonProps) {
  const { state, bookId, chapterNumber } = props;
  const { t } = useI18n();
  const chapterAnnotations =
    bookId && chapterNumber
      ? visibleChapterAnnotations(
          state.annotations,
          state.friends.friendIds.value,
          bookId,
          chapterNumber
        )
      : [];
  const noteCount = chapterAnnotations.length;

  if (noteCount === 0) {
    return null;
  }

  const label = t("chapter-notes-count", {
    defaultValue: "{{count}} notes for this chapter",
    count: noteCount,
  });

  const annotatedVerseNumbers = chapterAnnotations.flatMap((annotation) =>
    annotationVerseNumbers(annotation)
  );
  const firstAnnotatedVerse =
    annotatedVerseNumbers.length > 0
      ? Math.min(...annotatedVerseNumbers)
      : null;

  return (
    <button
      type="button"
      className="sb-bible-reader-mobile-header-notes"
      // Mirrors the account button below: stop the tap here so the reader
      // pane wrapper's pointerdown handler doesn't interfere.
      onPointerDown={(e: PointerEvent) => e.stopPropagation()}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        if (firstAnnotatedVerse === null || !bookId || !chapterNumber) {
          state.app.openDiscover();
          return;
        }
        if (!state.settings.settings.value.discoveredContent.showContent) {
          state.discover.view.value = "discover";
        }
        state.discover.scrollToVerse.value = {
          bookId,
          chapterNumber,
          verseNumber: firstAnnotatedVerse,
        };
      }}
      aria-label={label}
      title={label}
    >
      <span className="material-symbols-outlined" aria-hidden="true">
        sticky_note_2
      </span>
      <span className="sb-bible-reader-mobile-header-notes-count">
        {noteCount}
      </span>
    </button>
  );
}

interface VerseLine {
  indentLevel: number;
  parts: ChapterVerse["content"];
}

function getPoemIndentLevel(part: ChapterVerse["content"][0]) {
  if (
    part &&
    typeof part === "object" &&
    "text" in part &&
    typeof part.text === "string" &&
    typeof part.poem === "number" &&
    part.poem > 0
  ) {
    return part.poem;
  }

  return null;
}
// Move a footnote before an immediately preceding line break so the marker
// stays attached to the text it belongs to instead of starting on the next line.
function reorderFootnoteBeforeLineBreak(
  content: ChapterVerse["content"]
): ChapterVerse["content"] {
  let reorderedContent: ChapterVerse["content"] | undefined;

  for (let index = 0; index < content.length - 1; index += 1) {
    const currentPart = content[index]!;
    const nextPart = content[index + 1]!;

    if (isLineBreakPart(currentPart) && isFootnotePart(nextPart)) {
      reorderedContent ??= [...content];

      reorderedContent[index] = nextPart;
      reorderedContent[index + 1] = currentPart;

      index += 1;
    }
  }

  return reorderedContent ?? content;
}
function isLineBreakPart(
  part: ChapterVerse["content"][0] | undefined
): boolean {
  return (
    !!part &&
    typeof part === "object" &&
    "lineBreak" in part &&
    part.lineBreak === true
  );
}

function isFootnotePart(part: ChapterVerse["content"][0] | undefined) {
  return (
    !!part &&
    typeof part === "object" &&
    "noteId" in part &&
    typeof part.noteId === "number"
  );
}

type VerseSegment =
  | { type: "inline"; parts: ChapterVerse["content"] }
  | { type: "poetry"; lines: VerseLine[] };

interface ContentDecorationRange {
  start: number;
  end: number;
  className: string;
  style?: JSX.CSSProperties;
}

function getInlineText(part: ChapterVerse["content"][0]): string {
  if (typeof part === "string") {
    return part;
  }

  if (part && typeof part === "object" && "text" in part) {
    return typeof part.text === "string" ? part.text : "";
  }

  return "";
}

function getVersePlainText(content: ChapterVerse["content"]): string {
  return content.map((part) => getInlineText(part)).join("");
}

/**
 * A highlight resolved for one verse, plus where it came from. `broadcast` is
 * true for a highlight carried by a decoration — a session peer's, or an
 * extension's — as opposed to one the reader saved themselves.
 */
interface ResolvedHighlight {
  highlight: ChapterHighlight;
  broadcast: boolean;
}

function hasContentTargeting(decoration: VerseDecoration): boolean {
  const hasTargetContent =
    typeof decoration.targetContent === "string" &&
    decoration.targetContent.trim().length > 0;
  const hasIndexRange =
    typeof decoration.startIndex === "number" ||
    typeof decoration.endIndex === "number";

  return hasTargetContent || hasIndexRange;
}

function toContentDecorationRanges(
  verseText: string,
  decorations: VerseDecoration[]
): ContentDecorationRange[] {
  const verseLength = verseText.length;

  const clampIndex = (value: number) =>
    Math.max(0, Math.min(verseLength, Math.floor(value)));

  return decorations.flatMap((decoration) => {
    const className = decoration.className?.trim() ?? "";
    const style = decoration.style;

    const hasStart = typeof decoration.startIndex === "number";
    const hasEnd = typeof decoration.endIndex === "number";
    const windowStart = hasStart ? clampIndex(decoration.startIndex!) : 0;
    const windowEnd = hasEnd ? clampIndex(decoration.endIndex!) : verseLength;

    if (windowEnd <= windowStart) {
      return [];
    }

    const targetContent = decoration.targetContent?.trim();
    if (!targetContent) {
      return [
        {
          start: windowStart,
          end: windowEnd,
          className,
          style,
        },
      ];
    }

    const windowText = verseText.slice(windowStart, windowEnd);
    const ranges: ContentDecorationRange[] = [];
    let searchStart = 0;

    while (searchStart <= windowText.length) {
      const matchStartInWindow = windowText.indexOf(targetContent, searchStart);
      if (matchStartInWindow === -1) {
        break;
      }

      const absoluteStart = windowStart + matchStartInWindow;
      ranges.push({
        start: absoluteStart,
        end: absoluteStart + targetContent.length,
        className,
        style,
      });
      searchStart = matchStartInWindow + targetContent.length;
    }

    return ranges;
  });
}

function splitVerseIntoSegments(
  content: ChapterVerse["content"]
): VerseSegment[] {
  const segments: VerseSegment[] = [];
  let currentInlineParts: ChapterVerse["content"] = [];
  let currentPoetryLines: VerseLine[] = [];
  let currentPoetryLine: VerseLine = { indentLevel: 0, parts: [] };
  let inPoetry = false;

  const pushCurrentPoetryLine = () => {
    if (currentPoetryLine.parts.length > 0) {
      currentPoetryLines.push({
        indentLevel: currentPoetryLine.indentLevel,
        parts: [...currentPoetryLine.parts],
      });
      currentPoetryLine = {
        indentLevel: currentPoetryLine.indentLevel,
        parts: [],
      };
    }
  };

  const flushPoetry = () => {
    pushCurrentPoetryLine();
    if (currentPoetryLines.length > 0) {
      segments.push({ type: "poetry", lines: currentPoetryLines });
      currentPoetryLines = [];
    }
    currentPoetryLine = { indentLevel: 0, parts: [] };
    inPoetry = false;
  };

  const flushInline = () => {
    if (currentInlineParts.length > 0) {
      segments.push({ type: "inline", parts: currentInlineParts });
      currentInlineParts = [];
    }
  };

  for (const part of content) {
    const isFootnote = isFootnotePart(part);
    const indentLevel = getPoemIndentLevel(part);
    const isLineBreak = isLineBreakPart(part);

    if (isFootnote) {
      if (inPoetry) {
        currentPoetryLine.parts.push(part);
      } else {
        currentInlineParts.push(part);
      }
      continue;
    }

    if (indentLevel !== null) {
      if (!inPoetry) {
        flushInline();
        inPoetry = true;
      }
      if (
        currentPoetryLine.parts.length > 0 &&
        currentPoetryLine.indentLevel !== indentLevel
      ) {
        pushCurrentPoetryLine();
      }
      currentPoetryLine.indentLevel = indentLevel;
      currentPoetryLine.parts.push(part);
    } else if (isLineBreak) {
      if (inPoetry) {
        pushCurrentPoetryLine();
      } else {
        currentInlineParts.push(part);
      }
    } else {
      if (inPoetry) {
        flushPoetry();
      }
      currentInlineParts.push(part);
    }
  }

  if (inPoetry) {
    flushPoetry();
  } else {
    flushInline();
  }
  return segments;
}

function renderInlineContent(
  part: ChapterVerse["content"][0],
  index: number,
  onOpenFootnote: (noteId: number) => void,
  showHeadings: boolean,
  showFootnotes: boolean,
  showRedLettering: boolean,
  contentRanges: ContentDecorationRange[] = [],
  partStartIndex = 0,
  isBeforeFootnote = false
) {
  const splitTextByDecorations = (text: string) => {
    const partEndIndex = partStartIndex + text.length;
    const ranges = contentRanges
      .filter(
        (range) => range.end > partStartIndex && range.start < partEndIndex
      )
      .map((range) => ({
        start: Math.max(0, range.start - partStartIndex),
        end: Math.min(text.length, range.end - partStartIndex),
        className: range.className,
        style: range.style,
      }))
      .sort((left, right) => {
        if (left.start !== right.start) {
          return left.start - right.start;
        }
        return left.end - right.end;
      });

    if (ranges.length === 0) {
      return [
        {
          text,
          className: "",
          style: undefined as JSX.CSSProperties | undefined,
        },
      ];
    }

    const boundaries = new Set<number>([0, text.length]);
    for (const range of ranges) {
      boundaries.add(range.start);
      boundaries.add(range.end);
    }

    const sortedBoundaries = Array.from(boundaries).sort((a, b) => a - b);
    const segments: Array<{
      text: string;
      className: string;
      style?: JSX.CSSProperties;
    }> = [];

    for (let i = 0; i < sortedBoundaries.length - 1; i += 1) {
      const segmentStart = sortedBoundaries[i]!;
      const segmentEnd = sortedBoundaries[i + 1]!;
      if (segmentStart === segmentEnd) {
        continue;
      }

      const segmentText = text.slice(segmentStart, segmentEnd);
      if (!segmentText) {
        continue;
      }

      const activeRanges = ranges.filter(
        (range) => segmentStart >= range.start && segmentEnd <= range.end
      );
      const className = activeRanges
        .map((range) => range.className)
        .filter((name) => name.length > 0)
        .join(" ");
      const style = activeRanges.reduce<JSX.CSSProperties | undefined>(
        (merged, range) => {
          if (!range.style) {
            return merged;
          }

          return {
            ...(merged ?? {}),
            ...range.style,
          };
        },
        undefined
      );

      segments.push({
        text: segmentText,
        className,
        style,
      });
    }

    return segments;
  };

  if (typeof part === "string") {
    const text = isBeforeFootnote ? part.trimEnd() : part;
    const segments = splitTextByDecorations(text);
    return (
      <span key={index}>
        {segments.map((segment, segmentIndex) => (
          <span
            key={`${index}-${segmentIndex}`}
            className={segment.className}
            style={segment.style}
          >
            {segment.text}
          </span>
        ))}
      </span>
    );
  }

  if (!part || typeof part !== "object") {
    return null;
  }

  if ("text" in part && typeof part.text === "string") {
    let className = "";
    if (part.wordsOfJesus && showRedLettering) {
      className += " sb-words-of-jesus";
    }
    const text = isBeforeFootnote ? part.text.trimEnd() : part.text;
    const segments = splitTextByDecorations(text);
    return (
      <span key={index} className={className.trim()}>
        {segments.map((segment, segmentIndex) => (
          <span
            key={`${index}-${segmentIndex}`}
            className={segment.className}
            style={segment.style}
          >
            {(index > 0 ? " " : "") + segment.text}
          </span>
        ))}
      </span>
    );
  }

  if ("heading" in part && typeof part.heading === "string") {
    if (!showHeadings) {
      return null;
    }
    return <strong key={index}>{part.heading}</strong>;
  }

  if (isLineBreakPart(part)) {
    return <br key={index} />;
  }

  if ("noteId" in part && typeof part.noteId === "number") {
    if (!showFootnotes) {
      return <span key={index}> </span>;
    }
    return (
      <span key={index} className="sb-inline-footnote">
        {"\u00A0"}
        <button
          className="sb-inline-footnote-button"
          aria-label={`Open footnote ${part.noteId}`}
          title={`Open footnote ${part.noteId}`}
          onClick={(event: MouseEvent) => {
            event.stopPropagation();
            onOpenFootnote(part.noteId);
          }}
        >
          <span className="material-symbols-outlined">info</span>
        </button>
      </span>
    );
  }

  return null;
}

/**
 * Pointer type of the most recent `pointerdown` on a poetry verse's outer
 * span, read by that same verse's `onClick` guard below to decide how
 * forgiving its tap region is. Module scope rather than a ref: `renderVerseNode`
 * is a plain helper re-created on every call, not a component, so it has
 * nowhere of its own to persist state between the pointerdown and the click
 * that follows it (same reasoning as the module-scope state in
 * `app/flingSafeTap.ts`).
 */
let lastVersePointerType = "";

/** Clears the module-scope pointer-type state so tests cannot leak it between cases. */
export function resetLastVersePointerTypeForTests() {
  lastVersePointerType = "";
}

function renderChapterContent(
  chapterData: TranslationBookChapter | null,
  onVerseClick: (verse: BibleSelectedVerse, event: MouseEvent) => void,
  selectedVerses: BibleSelectedVerse[],
  onOpenFootnote: (noteId: number, verse: ChapterVerse | null) => void,
  highlights: ChapterHighlight[],
  decorations: VerseDecoration[],
  chapterAnnotations: Annotation[],
  scriptureElements: ScriptureElementsBehavior,
  onAnnotationVerseClick: (
    verse: BibleSelectedVerse,
    verseNumber: number,
    event: MouseEvent
  ) => void
) {
  if (!chapterData) {
    return null;
  }

  const getVerseDecorations = (verseNumber: number) => {
    return decorations.filter(
      (decoration) =>
        (!decoration.translationId ||
          decoration.translationId === chapterData.translation.id) &&
        decoration.bookId === chapterData.book.id &&
        decoration.chapterNumber === chapterData.chapter.number &&
        decoration.verses.includes(verseNumber)
    );
  };

  // Decorations asking to be drawn as highlights (`decoration.highlight`),
  // flattened to one entry per verse. Content-targeted decorations are skipped:
  // the ribbon layer works per verse-run and can't paint a text fragment.
  // Later decorations win, matching how their CSS is layered below.
  const decorationHighlights = new Map<number, ChapterHighlight>();
  for (const decoration of decorations) {
    if (!decoration.highlight || hasContentTargeting(decoration)) {
      continue;
    }
    if (
      (decoration.translationId &&
        decoration.translationId !== chapterData.translation.id) ||
      decoration.bookId !== chapterData.book.id ||
      decoration.chapterNumber !== chapterData.chapter.number
    ) {
      continue;
    }
    for (const verseNumber of decoration.verses) {
      decorationHighlights.set(verseNumber, {
        ...decoration.highlight,
        verse: verseNumber,
      });
    }
  }

  // `showHighlights` hides the reader's *saved* highlights. Decoration
  // highlights are a live signal from a session peer or an extension, so they
  // stay visible either way — as they did when they were plain CSS.
  //
  // `broadcast` distinguishes the two for rendering: a decoration highlight is
  // drawn as an outline, a saved one as a solid ribbon. A broadcast covers the
  // reader's own highlight rather than replacing it, so the outline is what
  // says "this isn't yours, and yours is still underneath".
  const getVerseHighlight = (verseNumber: number): ResolvedHighlight | null => {
    const decorated = decorationHighlights.get(verseNumber);
    if (decorated) {
      return { highlight: decorated, broadcast: true };
    }

    if (!scriptureElements.showHighlights) {
      return null;
    }

    for (const highlight of highlights) {
      if (typeof highlight.verse === "number") {
        if (highlight.verse === verseNumber) {
          return { highlight, broadcast: false };
        }
        continue;
      }

      const [start, end] = highlight.verse;
      if (verseNumber >= start && verseNumber <= end) {
        return { highlight, broadcast: false };
      }
    }

    return null;
  };

  // The highlight background is drawn behind the text by the ribbon layer (see
  // ChapterContent), so a highlighted run's wrapper paints no background itself.
  // It only carries the readable font color and a `fill` (a CSS-var reference for
  // preset colors, or the custom hex) that the layer reads back off the DOM.
  const getHighlightPresentation = (resolved: ResolvedHighlight | null) => {
    if (!resolved) {
      return {
        className: "",
        style: undefined as JSX.CSSProperties | undefined,
        fill: null as string | null,
        broadcast: false,
      };
    }

    const { highlight, broadcast } = resolved;

    // A custom colour stands on its own — the font colour is optional and the
    // text inherits when it's absent. Requiring both meant a highlight with
    // only a custom colour silently rendered as its preset instead.
    if (highlight.customColor) {
      return {
        className: "sb-highlight",
        style: highlight.customFontColor
          ? ({ color: highlight.customFontColor } as JSX.CSSProperties)
          : undefined,
        fill: highlight.customColor,
        broadcast,
      };
    }

    // The `transparent` fallback matters: a colorId with no matching theme
    // variable would otherwise make `fill` invalid at computed-value time, and
    // `fill` inherits down to its initial value of black — a solid black bar
    // behind the verse. Colour ids don't all come from our own picker (an
    // extension can pass one through from a chat message), so an unrecognised
    // one has to fail invisible.
    return {
      className: `sb-highlight sb-highlight-${highlight.colorId}`,
      style: undefined as JSX.CSSProperties | undefined,
      fill: `var(--sb-highlight-${highlight.colorId}-color, transparent)`,
      broadcast,
    };
  };

  const getDecorationPresentation = (verseDecorations: VerseDecoration[]) => {
    const matchingDecorations = verseDecorations.filter((decoration) => {
      return !hasContentTargeting(decoration);
    });

    return matchingDecorations.reduce(
      (presentation, decoration) => ({
        className: decoration.className
          ? `${presentation.className} ${decoration.className}`
          : presentation.className,
        style: decoration.style
          ? {
              ...(presentation.style ?? {}),
              ...decoration.style,
            }
          : presentation.style,
      }),
      {
        className: "",
        style: undefined as JSX.CSSProperties | undefined,
      }
    );
  };

  // Also keys on `broadcast`, so a broadcast highlight never merges into one run
  // with a saved highlight of the same colour — they draw differently.
  const getHighlightColorKey = (resolved: ResolvedHighlight | null) => {
    if (!resolved) {
      return null;
    }
    const { highlight, broadcast } = resolved;
    const prefix = broadcast ? "broadcast:" : "";
    if (highlight.customColor) {
      return `${prefix}custom:${highlight.customColor}:${highlight.customFontColor ?? ""}`;
    }
    return `${prefix}${highlight.colorId}`;
  };

  // Only matches an annotation to the verse number it *starts* at (the
  // lowest verse it targets), not every verse it spans — a Genesis 1:3-6
  // note marks verse 3 only, not 4, 5, and 6 too.
  const getVerseAnnotations = (verseNumber: number): Annotation[] =>
    chapterAnnotations.filter((annotation) => {
      const verseNumbers = annotationVerseNumbers(annotation);
      return (
        verseNumbers.length > 0 && Math.min(...verseNumbers) === verseNumber
      );
    });

  // Renders a verse's number when shown, boxed if the verse has a covering
  // annotation; when verse numbers are hidden, an annotated verse still shows
  // a `sticky_note_2` icon in that spot so the indicator survives the setting.
  // An annotated number/icon is clickable, jumping straight to its note.
  const renderVerseNumberOrIcon = (
    verseNumber: number,
    verse: BibleSelectedVerse
  ) => {
    const hasAnnotation = getVerseAnnotations(verseNumber).length > 0;
    const handleAnnotationClick = (event: MouseEvent) =>
      onAnnotationVerseClick(verse, verseNumber, event);

    if (scriptureElements.showVerseNumbers) {
      return (
        <sup
          className={
            hasAnnotation
              ? "sb-verse-number sb-verse-number-annotated"
              : "sb-verse-number"
          }
          onClick={hasAnnotation ? handleAnnotationClick : undefined}
          role={hasAnnotation ? "button" : undefined}
          tabIndex={hasAnnotation ? 0 : undefined}
        >
          {verseNumber}
        </sup>
      );
    }
    if (!hasAnnotation) {
      return null;
    }
    return (
      <sup
        className="sb-verse-number sb-verse-annotation-icon"
        onClick={handleAnnotationClick}
        role="button"
        tabIndex={0}
      >
        <span className="material-symbols-outlined">sticky_note_2</span>
      </sup>
    );
  };

  // Renders a single verse's `<span class="sb-verse">`. The highlight background
  // is never painted here — an enclosing run wrapper (below) carries it and the
  // ribbon layer draws it behind the text. Verse decorations still apply here.
  const renderVerseNode = (value: ChapterVerse, entryIndex: number) => {
    const verse: BibleSelectedVerse = {
      bookId: chapterData.book.id,
      chapterNumber: chapterData.chapter.number,
      verse: value,
      translationId: chapterData.translation.id,
    };
    const isSelected = selectedVerses.some(
      (v) =>
        v.verse.number === value.number &&
        v.bookId === chapterData.book.id &&
        v.chapterNumber === chapterData.chapter.number
    );
    const content = reorderFootnoteBeforeLineBreak(value.content);
    const segments = splitVerseIntoSegments(content);
    const hasPoetry = segments.some((s) => s.type === "poetry");
    const verseDecorations = getVerseDecorations(value.number);
    const decorationPresentation = getDecorationPresentation(verseDecorations);
    const contentDecorations = verseDecorations.filter((decoration) =>
      hasContentTargeting(decoration)
    );
    const contentRanges = toContentDecorationRanges(
      getVersePlainText(value.content),
      contentDecorations
    );
    let currentTextOffset = 0;
    const getPartTextStartIndex = (part: ChapterVerse["content"][0]) => {
      const startIndex = currentTextOffset;
      currentTextOffset += getInlineText(part).length;
      return startIndex;
    };
    const verseClassName = [
      "sb-verse",
      hasPoetry ? "sb-verse-poetry" : "",
      isSelected ? "sb-verse-selected" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const verseDecoratorClassName = [
      "sb-verse-decorator",
      decorationPresentation.className.trim(),
    ]
      .filter(Boolean)
      .join(" ");
    const verseDecoratorStyle = {
      ...(decorationPresentation.style ?? {}),
    };

    if (hasPoetry) {
      return (
        <span
          key={`verse-${entryIndex}`}
          className={verseClassName}
          data-verse-number={value.number}
          onPointerDown={(event: PointerEvent) => {
            lastVersePointerType = event.pointerType;
          }}
          onClick={(event: MouseEvent) => {
            // Poetry lines are `display: block` so each one spans the full
            // content width — a tap in the blank margin past a short line's
            // last word still lands inside this outer span even though it's
            // nowhere near the verse's actual text. On a mouse, where a
            // precise miss is unambiguous, only a click that reaches an
            // actual `.sb-verse-decorator` (the inline span the rendered
            // words themselves sit in) counts as selecting the verse — the
            // reader's outside-click handling (`BibleReaderToolbar.tsx`) is
            // then free to treat the rest of the block as a dismiss. A touch
            // tap is far less precise, though, and there's no in-between
            // "blank space" for a finger to miss into that a mouse pointer
            // couldn't also land on deliberately — so a touch keeps the
            // original, forgiving behavior of the whole block.
            const target = event.target as HTMLElement | null;
            const verseTapSelector =
              lastVersePointerType === "touch"
                ? ".sb-verse"
                : ".sb-verse-decorator";
            if (!target?.closest(verseTapSelector)) return;
            onVerseClick(verse, event);
          }}
          style={{
            cursor: "pointer",
          }}
          role="button"
          tabIndex={0}
        >
          {segments.map((segment, segIndex) => {
            if (segment.type === "inline") {
              return (
                <span
                  key={`verse-${entryIndex}-seg-${segIndex}-inline`}
                  className={verseDecoratorClassName}
                  style={verseDecoratorStyle}
                >
                  {segIndex === 0 &&
                    renderVerseNumberOrIcon(value.number, verse)}
                  {segment.parts.map((part, partIndex) =>
                    renderInlineContent(
                      part,
                      segIndex * 10000 + partIndex,
                      (noteId) => onOpenFootnote(noteId, value),
                      scriptureElements.showHeadings,
                      scriptureElements.showFootnotes,
                      scriptureElements.showRedLettering,
                      contentRanges,
                      getPartTextStartIndex(part),
                      isFootnotePart(segment.parts[partIndex + 1])
                    )
                  )}
                </span>
              );
            }
            return segment.lines.map((line, lineIndex) => (
              <span
                key={`verse-${entryIndex}-seg-${segIndex}-line-${lineIndex}`}
                className="sb-verse-line"
                style={{
                  paddingInlineStart:
                    line.indentLevel > 0
                      ? `${line.indentLevel * 30}px`
                      : undefined,
                }}
              >
                <span
                  className={verseDecoratorClassName}
                  style={verseDecoratorStyle}
                >
                  {segIndex === 0 &&
                    lineIndex === 0 &&
                    renderVerseNumberOrIcon(value.number, verse)}
                  {line.parts.map((part, partIndex) =>
                    renderInlineContent(
                      part,
                      partIndex,
                      (noteId) => onOpenFootnote(noteId, value),
                      scriptureElements.showHeadings,
                      scriptureElements.showFootnotes,
                      scriptureElements.showRedLettering,
                      contentRanges,
                      getPartTextStartIndex(part),
                      isFootnotePart(line.parts[partIndex + 1])
                    )
                  )}
                </span>
              </span>
            ));
          })}
        </span>
      );
    }

    return (
      <span
        key={`verse-${entryIndex}`}
        className={verseClassName}
        data-verse-number={value.number}
        onClick={(event: MouseEvent) => {
          onVerseClick(verse, event);
        }}
        style={{
          cursor: "pointer",
        }}
        role="button"
        tabIndex={0}
      >
        <span className={verseDecoratorClassName} style={verseDecoratorStyle}>
          {renderVerseNumberOrIcon(value.number, verse)}
          {content.map((part, index) =>
            renderInlineContent(
              part,
              index,
              (noteId) => onOpenFootnote(noteId, value),
              scriptureElements.showHeadings,
              scriptureElements.showFootnotes,
              scriptureElements.showRedLettering,
              contentRanges,
              getPartTextStartIndex(part),
              isFootnotePart(content[index + 1])
            )
          )}
        </span>
      </span>
    );
  };

  const isVerseEntry = (entry: unknown): entry is ChapterVerse =>
    !!entry &&
    typeof entry === "object" &&
    (entry as { type?: unknown }).type === "verse" &&
    typeof (entry as ChapterVerse).number === "number" &&
    Array.isArray((entry as ChapterVerse).content);

  const entries = chapterData.chapter.content;
  const nodes: (JSX.Element | null)[] = [];

  // Verse text carries no leading/trailing spaces of its own — with numbers on,
  // the number's own margins are what keep one verse off the back of the
  // previous one. Hide the numbers and adjacent verses collide
  // ("...had your fill.Do not work..."), so emit a real space between them.
  // It sits between the verse spans rather than inside one, so highlight
  // ribbons and verse selection still stop at a verse's own glyphs, and it
  // collapses away at a line break like any other space.
  const needsVerseSpacing = !scriptureElements.showVerseNumbers;
  let previousWasVerse = false;

  // Keyed so the separator is a first-class sibling of the keyed verses it sits
  // between, rather than an unkeyed string mixed in among them. A fragment adds
  // nothing to the DOM — what renders is the bare text node either way.
  const verseSeparator = (key: string) => <Fragment key={key}> </Fragment>;

  for (let i = 0; i < entries.length; ) {
    const entry = entries[i];

    if (!entry || typeof entry !== "object") {
      nodes.push(null);
      i += 1;
      continue;
    }

    if (entry.type === "heading" && Array.isArray(entry.content)) {
      if (!scriptureElements.showHeadings) {
        nodes.push(null);
        i += 1;
        continue;
      }
      const heading = (entry.content as unknown[])
        .filter((item) => typeof item === "string")
        .join(" ");
      nodes.push(
        <h3 key={`heading-${i}`} className="sb-chapter-heading">
          {heading}
        </h3>
      );
      previousWasVerse = false;
      i += 1;
      continue;
    }

    if (entry.type === "line_break") {
      nodes.push(<div key={`break-${i}`} className="sb-line-break" />);
      previousWasVerse = false;
      i += 1;
      continue;
    }

    if (entry.type === "hebrew_subtitle" && Array.isArray(entry.content)) {
      nodes.push(
        <p key={`subtitle-${i}`} className="sb-subtitle">
          {entry.content.map((part, index) =>
            renderInlineContent(
              part,
              index,
              (noteId) => onOpenFootnote(noteId, null),
              scriptureElements.showHeadings,
              scriptureElements.showFootnotes,
              scriptureElements.showRedLettering,
              [],
              0,
              isFootnotePart(entry.content[index + 1])
            )
          )}
        </p>
      );
      previousWasVerse = false;
      i += 1;
      continue;
    }

    if (isVerseEntry(entry)) {
      const highlight = getVerseHighlight(entry.number);
      const colorKey = getHighlightColorKey(highlight);

      if (needsVerseSpacing && previousWasVerse) {
        nodes.push(verseSeparator(`space-${i}`));
      }
      previousWasVerse = true;

      if (colorKey === null) {
        nodes.push(renderVerseNode(entry, i));
        i += 1;
        continue;
      }

      const isPoetry = splitVerseIntoSegments(entry.content).some(
        (s) => s.type === "poetry"
      );

      // Every highlighted unit is wrapped in a `display: contents` element that
      // carries the fill (for the ribbon layer) and font color. Contiguous
      // same-color PROSE verses are grouped into one wrapper so the layer draws
      // a single continuous ribbon across them. Poetry stays one verse per
      // wrapper: its indented lines already read as a connected shape, and
      // merging block-level verses would be visually noisy.
      const runIndices = [i];
      let j = i + 1;
      if (!isPoetry) {
        while (j < entries.length) {
          const next = entries[j];
          if (!isVerseEntry(next)) {
            break;
          }
          const nextKey = getHighlightColorKey(getVerseHighlight(next.number));
          const nextIsPoetry = splitVerseIntoSegments(next.content).some(
            (s) => s.type === "poetry"
          );
          if (nextKey !== colorKey || nextIsPoetry) {
            break;
          }
          runIndices.push(j);
          j += 1;
        }
      }

      const presentation = getHighlightPresentation(highlight);
      // Ribbon key: the run's verse range. Stable across reflow/recolor (same
      // verses -> same key -> reused), so those don't churn the element; fades
      // are decided from coverage, not this key (see `measureRibbons`). `i` is
      // the run's first entry; its last is runIndices' tail.
      const firstVerse = (entries[i] as ChapterVerse).number;
      const lastIdx = runIndices[runIndices.length - 1]!;
      const lastVerse = (entries[lastIdx] as ChapterVerse).number;
      const runKey = `${firstVerse}-${lastVerse}`;
      nodes.push(
        <span
          key={`highlight-run-${i}`}
          className={presentation.className}
          style={presentation.style}
          data-highlight-fill={presentation.fill ?? undefined}
          data-highlight-key={runKey}
          data-highlight-broadcast={presentation.broadcast ? "true" : undefined}
        >
          {runIndices.flatMap((idx, runIndex) => {
            const verseNode = renderVerseNode(
              entries[idx] as ChapterVerse,
              idx
            );
            // Same separator as between top-level verses; inside a run it falls
            // within the ribbon, which is correct — the whole run is one fill.
            return needsVerseSpacing && runIndex > 0
              ? [verseSeparator(`space-${idx}`), verseNode]
              : [verseNode];
          })}
        </span>
      );
      i = j;
      continue;
    }

    nodes.push(null);
    i += 1;
  }

  return nodes;
}

interface BibleReaderProps {
  currentSlot: TabSlot;
  readingState: BibleReadingState;
  selectorState: BibleSelectorState;
  scriptureElements?: ScriptureElementsBehavior;
  state?: SeedBibleState;
  mobileChrome?: BibleReaderMobileChromeProps;
  /** The shared session backing this tab, if any — drives the mobile header
   * participants stack. Null/undefined for a normal, non-shared tab. */
  sharedSession?: BibleReadingSession | null;

  readingPlanBelongs?: ComponentChildren;
}

export interface BibleReaderMobileChromeProps {
  isScrolled: boolean;
  prevChapterPreview: TranslationBookChapter | null;
  nextChapterPreview: TranslationBookChapter | null;
  showMobileSettings: boolean;
  onOpenMobileSettings: () => void;
  onCloseMobileSettings: () => void;
  onOpenAllSettings: () => void;
  // Plain refs: these only need `.current` filled in, which Preact does for a
  // ref object on its own.
  swipeViewportRef: RefObject<HTMLDivElement>;
  swipeTrackRef: RefObject<HTMLDivElement>;
  // A callback because it feeds component state, not just a ref.
  currentScrollerRefCallback: (el: HTMLDivElement | null) => void;
  /**
   * Rendered inside the scrolling chapter panel, after the passage. On mobile
   * the panel is the scroll container, so anything placed here is reached by
   * scrolling to the end of the chapter rather than sitting over the text.
   */
  belowContent?: ComponentChildren;
}

function renderStaticChapterContent(
  chapter: TranslationBookChapter | null,
  scriptureElements: ScriptureElementsBehavior
) {
  if (!chapter) return null;
  return renderChapterContent(
    chapter,
    () => {},
    [],
    () => {},
    [],
    [],
    [],
    scriptureElements,
    () => {}
  );
}

// One drawn highlight ribbon. `key` is the run's verse range ("5-8"); `first`/
// `last` are it as numbers (coverage checks). `enter` = fade in (a new highlight,
// not a reshape); `exiting` = fading out before removal.
interface Ribbon {
  key: string;
  d: string;
  fill: string;
  broadcast: boolean;
  first: number;
  last: number;
  enter: boolean;
  exiting: boolean;
}
const RIBBON_FADE_MS = 250;

/**
 * How long the chapter the reader has left stays on screen, dimmed, before the
 * placeholder takes over.
 *
 * Swapping to the placeholder the instant you navigate reads as a flicker on a
 * fast connection, where the new text lands in well under this. Dimming costs
 * nothing and moves nothing, so it carries the common case; the placeholder is
 * only for waits long enough that dimmed text starts to look stuck.
 *
 * Does not apply on a cold start — with no chapter on screen there is nothing
 * to dim, so the placeholder shows straight away.
 */
export const CHAPTER_SKELETON_DELAY_MS = 500;

/**
 * Bar widths for the chapter loading placeholder, one array per paragraph.
 *
 * Hand-picked rather than random so the placeholder is identical on every
 * render — a fresh set each time would shimmer *and* reflow — and so it reads
 * as ragged prose rather than a block.
 *
 * Deliberately more paragraphs than the tallest reading pane needs. The two
 * failure modes are not symmetric: falling short leaves visible dead space
 * below the bars, while overshooting spills below the fold where nobody sees
 * it (the pane scrolls internally, and scroll position resets on every chapter
 * change). Tuning the fill is editing this one array — no measurement, and no
 * reflow on a placeholder that remounts on every navigation.
 */
const CHAPTER_SKELETON_PARAGRAPHS = [
  ["97%", "92%", "99%", "88%", "71%"],
  ["94%", "99%", "90%", "96%", "58%"],
  ["99%", "89%", "95%", "93%", "77%"],
  ["91%", "98%", "87%", "96%", "64%"],
  ["96%", "93%", "99%", "85%", "80%"],
  ["98%", "90%", "94%", "97%", "52%"],
  ["93%", "99%", "91%", "88%", "74%"],
  ["95%", "96%", "98%", "89%", "68%"],
] as const;

/** A note marker in the mobile gutter, placed against a measured line box. */
interface NoteMarker {
  verseNumber: number;
  /** Offset from the top of the chapter content box, in px. */
  top: number;
  /** Height of the verse's first line box, so the icon centres on it. */
  height: number;
}

/**
 * One other participant's place in this chapter, ready to draw: the verses
 * they can see plus what identifies them. Built in the reader from the shared
 * session and measured into a bar by ChapterContent (#1692).
 */
export interface ParticipantPresence {
  connectionId: string;
  displayName: string;
  imageUrl: string | null;
  visual: ConnectionSessionUserVisual;
  firstVerse: number;
  lastVerse: number;
}

/** A presence bar, placed against measured line boxes. */
interface PresenceMarker {
  connectionId: string;
  displayName: string;
  imageUrl: string | null;
  visual: ConnectionSessionUserVisual;
  /** Offset from the top of the chapter content box, in px. */
  top: number;
  height: number;
  /**
   * Which column of the gutter this bar sits in. People reading the same
   * verses would otherwise be drawn on top of each other, hiding everyone but
   * whoever was painted last, so bars whose spans overlap are dealt out into
   * side-by-side lanes. Zero whenever nobody overlaps. A lane at or beyond
   * `PRESENCE_MAX_LANES` has no room in the gutter and draws no bar.
   */
  lane: number;
}

/**
 * How many bars fit side by side in the presence gutter. The gutter is a fixed
 * width so the text never shifts as people come and go, which leaves a fourth
 * person on the same verses nowhere to draw a bar: they keep their place in
 * the avatar stack and only the bar is left out.
 */
const PRESENCE_MAX_LANES = 3;
/**
 * Where each lane sits relative to the gutter's centre line, in bar widths
 * with gaps. Lane zero is centred so a lone bar lines up under its avatar, and
 * later lanes fill in on either side of it.
 */
const PRESENCE_LANE_OFFSETS = [0, 1, -1] as const;
/** Avatars a stack shows before collapsing the rest into a "+N" chip. */
const PRESENCE_MAX_STACK_AVATARS = 3;
/**
 * Pixel geometry of the gutter's avatars and arrows. The stacks and arrows
 * are placed in JavaScript against measured verse positions, so these are
 * mirrored by the matching `--sb-presence-*` variables in the stylesheet.
 */
const PRESENCE_AVATAR_PX = 20;
/** Vertical distance between one avatar of a stack and the next. */
const PRESENCE_STACK_STEP_PX = 12;
/** Room kept above a pinned stack for the "continues above" arrow. */
const PRESENCE_PIN_INSET_PX = 14;
const PRESENCE_ARROW_PX = 6;
const PRESENCE_EDGE_INSET_PX = 4;

/** The part of the chapter content box that is on screen, in content px. */
interface PresenceViewport {
  top: number;
  bottom: number;
}

/**
 * One or more avatars drawn together at a point in the gutter. Avatars that
 * would land on top of one another (their owners' verses start on the same
 * line, or several bars are pinned at the top of the screen at once) are
 * collapsed into a single stack, the way the mobile participants list does.
 */
interface PresenceAvatarStack {
  key: string;
  /** Offset from the top of the chapter content box, in px. */
  top: number;
  /**
   * Held at the top of the screen because its owners' verses start above it.
   * A pinned stack is re-placed on every scroll frame, so it must not glide.
   */
  pinned: boolean;
  markers: PresenceMarker[];
}

interface PresenceArrow {
  key: string;
  direction: "up" | "down";
  top: number;
  lane: number;
  color: string;
}

/**
 * Anchors each participant's avatar to the top of their bar, then holds it at
 * the top of the screen once that has scrolled out of view, so the avatar
 * follows the reader down the page for as long as its owner's verses do. It
 * lets go at the bottom of the bar rather than leaving the verses it belongs
 * to. Avatars that end up overlapping are merged into one stack.
 */
function stackPresenceAvatars(
  markers: PresenceMarker[],
  viewport: PresenceViewport | null
): PresenceAvatarStack[] {
  const anchored = markers.map((marker) => {
    const lowest = marker.top + marker.height - PRESENCE_AVATAR_PX;
    let top = marker.top;
    let pinned = false;
    if (viewport) {
      const pinTop = viewport.top + PRESENCE_PIN_INSET_PX;
      if (pinTop > marker.top && pinTop <= lowest) {
        top = pinTop;
        pinned = true;
      } else if (pinTop > lowest && lowest > marker.top) {
        top = lowest;
      }
    }
    return { marker, top, pinned };
  });
  anchored.sort(
    (a, b) =>
      a.top - b.top ||
      a.marker.connectionId.localeCompare(b.marker.connectionId)
  );

  const stacks: PresenceAvatarStack[] = [];
  // Bottom edge of the stack being built, below which the next avatar is on
  // its own again.
  let reach = -Infinity;
  for (const entry of anchored) {
    const current = stacks[stacks.length - 1];
    if (current && entry.top < reach) {
      current.markers.push(entry.marker);
      const shown = Math.min(
        current.markers.length,
        PRESENCE_MAX_STACK_AVATARS
      );
      const rows = shown + (current.markers.length > shown ? 1 : 0);
      reach =
        current.top + PRESENCE_AVATAR_PX + (rows - 1) * PRESENCE_STACK_STEP_PX;
      continue;
    }
    stacks.push({
      key: "",
      top: entry.top,
      pinned: entry.pinned,
      markers: [entry.marker],
    });
    reach = entry.top + PRESENCE_AVATAR_PX;
  }
  for (const stack of stacks) {
    stack.key = stack.markers.map((m) => m.connectionId).join("+");
  }
  return stacks;
}

/**
 * A small arrow at the top or bottom edge of the screen for every bar that
 * carries on past it, so the reader can tell that a participant sees more than
 * the part of their bar on this screen. Only bars with a lane to draw in get
 * one, since there is nothing on screen for the arrow to continue.
 */
function placePresenceArrows(
  markers: PresenceMarker[],
  viewport: PresenceViewport | null
): PresenceArrow[] {
  if (!viewport) return [];
  const arrows: PresenceArrow[] = [];
  for (const marker of markers) {
    if (marker.lane >= PRESENCE_MAX_LANES) continue;
    const bottom = marker.top + marker.height;
    if (marker.top < viewport.top && bottom > viewport.top) {
      arrows.push({
        key: `${marker.connectionId}:up`,
        direction: "up",
        top: viewport.top + PRESENCE_EDGE_INSET_PX,
        lane: marker.lane,
        color: marker.visual.color,
      });
    }
    if (bottom > viewport.bottom && marker.top < viewport.bottom) {
      arrows.push({
        key: `${marker.connectionId}:down`,
        direction: "down",
        top: viewport.bottom - PRESENCE_EDGE_INSET_PX - PRESENCE_ARROW_PX,
        lane: marker.lane,
        color: marker.visual.color,
      });
    }
  }
  return arrows;
}

/**
 * Where the screen is over the chapter content, in the content's own
 * coordinates. See `measureVisibleReaderBounds` for what counts as on screen.
 */
function measurePresenceViewport(
  content: HTMLElement,
  scroller: HTMLElement | null
): PresenceViewport {
  const contentRect = content.getBoundingClientRect();
  const { top, bottom } = measureVisibleReaderBounds(content, scroller);
  return { top: top - contentRect.top, bottom: bottom - contentRect.top };
}

/**
 * The gutter down the start edge of the chapter showing where the other
 * participants are (#1692): a bar per person spanning the verses they can
 * see, their avatar at the top of it (held on screen as the reader scrolls
 * past), and arrows where a bar runs off the screen.
 *
 * Its own component so that following the scroll only re-renders the gutter.
 * The viewport is re-measured on every scroll frame, and doing that in
 * ChapterContent would re-render the whole chapter's text each time.
 */
function PresenceGutter({
  markers,
  chapterKey,
  contentRef,
}: {
  markers: PresenceMarker[];
  chapterKey: string;
  contentRef: RefObject<HTMLDivElement>;
}) {
  const [viewport, setViewport] = useState<PresenceViewport | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);

  const measure = () => {
    const content = contentRef.current;
    if (!content) return;
    const next = measurePresenceViewport(content, scrollerRef.current);
    setViewport((prev) =>
      prev && prev.top === next.top && prev.bottom === next.bottom ? prev : next
    );
  };
  const measureRef = useRef(measure);
  measureRef.current = measure;

  // Declared before the per-render measurement below so the scroller is known
  // by the time the first viewport is read, instead of a page-sized guess that
  // the next frame has to correct.
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    scrollerRef.current = findScrollContainer(content);
    const target: EventTarget = scrollerRef.current ?? window;
    let frame: number | null = null;
    const schedule = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        measureRef.current();
      });
    };
    target.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      target.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [contentRef]);

  // After every render: the markers only change once the content has been
  // laid out, and the viewport has to be read against that same layout.
  useLayoutEffect(() => {
    measureRef.current();
  });

  const stacks = stackPresenceAvatars(markers, viewport);
  const arrows = placePresenceArrows(markers, viewport);

  return (
    <div className="sb-presence-gutter" aria-hidden="true">
      {markers
        .filter((marker) => marker.lane < PRESENCE_MAX_LANES)
        .map((marker) => (
          <div
            // Keyed by chapter as well as owner so a navigation gives every
            // bar a fresh element. A bar glides between positions within a
            // chapter (see the transition in the stylesheet), and the whole
            // page's text is replaced on a navigation — a bar left to travel
            // from its old verses to its new ones would be gliding across
            // scripture it was never measured against.
            key={`${chapterKey}:${marker.connectionId}`}
            className="sb-presence-marker"
            style={{
              top: `${marker.top}px`,
              height: `${marker.height}px`,
              background: marker.visual.color,
              "--sb-presence-lane-offset": PRESENCE_LANE_OFFSETS[marker.lane],
            }}
          />
        ))}
      {arrows.map((arrow) => (
        <span
          key={`${chapterKey}:${arrow.key}`}
          className={`sb-presence-arrow sb-presence-arrow-${arrow.direction}`}
          style={{
            top: `${arrow.top}px`,
            "--sb-presence-lane-offset": PRESENCE_LANE_OFFSETS[arrow.lane],
            "--sb-presence-arrow-color": arrow.color,
          }}
        />
      ))}
      {stacks.map((stack) => {
        const shown = stack.markers.slice(0, PRESENCE_MAX_STACK_AVATARS);
        const overflow = stack.markers.length - shown.length;
        return (
          <div
            key={`${chapterKey}:${stack.key}`}
            className={`sb-presence-avatars${
              stack.pinned ? " sb-presence-avatars-pinned" : ""
            }`}
            style={{ top: `${stack.top}px` }}
          >
            {shown.map((marker) => (
              <span key={marker.connectionId} className="sb-presence-avatar">
                <Avatar
                  imageUrl={marker.imageUrl}
                  visual={marker.visual}
                  title={marker.displayName}
                />
              </span>
            ))}
            {overflow > 0 && (
              <span className="sb-presence-avatars-more">+{overflow}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface ChapterContentProps {
  chapterData: Signal<TranslationBookChapter | null>;
  chapterDataPromise: Promise<void>;
  initialChapterLoadSettled: ReadonlySignal<boolean>;
  selectedVerses: Signal<BibleSelectedVerse[]>;
  highlights: ReadonlySignal<ChapterHighlights>;
  decorations: ReadonlySignal<VerseDecoration[]>;
  annotations?: AnnotationsManager;
  /** Whose notes besides the user's own get verse markers. */
  friends?: FriendsManager;
  selectVerse: (
    verse: BibleSelectedVerse,
    selectionX: number,
    selectionY: number
  ) => void;
  selectVersesFromTextSelection: () => void;
  justConvertedSelectionRef: { current: boolean };
  selectFootnote: (noteId: number | null) => void;
  scriptureElements: ScriptureElementsBehavior;
  onAnnotationVerseClick: (
    verse: BibleSelectedVerse,
    verseNumber: number,
    event: MouseEvent
  ) => void;
  /**
   * True while this is the chapter the reader has *left* — shown dimmed until
   * the chapter they navigated to arrives.
   */
  isStale?: boolean;
  /**
   * Mobile has no Discover panel to read notes alongside the text, so the
   * note markers move out into a gutter beside the scripture there (#1691).
   */
  isMobile?: boolean;
  /**
   * Other participants reading this same chapter in a shared session, drawn as
   * bars beside the text. Undefined outside a session, which closes the gutter
   * altogether; inside one the gutter stays open even while the list is empty,
   * so the text never shifts as people arrive in and leave the chapter.
   */
  presence?: ParticipantPresence[];
  /**
   * Called as the reader scrolls with the span of verses on screen, or null
   * when none are. Drives the presence this client publishes.
   */
  onVisibleVersesChange?: (range: VisibleVerseRange | null) => void;
}

function ChapterContent(props: ChapterContentProps) {
  const {
    chapterData,
    chapterDataPromise,
    initialChapterLoadSettled,
    selectedVerses,
    highlights,
    decorations,
    annotations,
    friends,
    selectVerse,
    selectFootnote,
    selectVersesFromTextSelection,
    justConvertedSelectionRef,
    scriptureElements,
    onAnnotationVerseClick,
    isMobile = false,
    presence,
    onVisibleVersesChange,
  } = props;

  const { t } = useI18n();
  const currentChapter = chapterData.value;
  const chapterAnnotations =
    currentChapter && annotations
      ? visibleChapterAnnotations(
          annotations,
          friends?.friendIds.value ?? [],
          currentChapter.book.id,
          currentChapter.chapter.number
        )
      : [];

  const contentRef = useRef<HTMLDivElement>(null);
  const [ribbons, setRibbons] = useState<Ribbon[]>([]);
  // What's on screen (including ribbons fading out) so the reconcile can diff.
  const renderedRef = useRef<Ribbon[]>([]);
  // Fade-out removal timers, keyed by ribbon key.
  const exitTimers = useRef<Map<string, number>>(new Map());
  // Verses highlighted (any color) last measure; distinguishes new from reshaped.
  const prevCoverageRef = useRef<Set<number>>(new Set());
  // Identity of the chapter last measured. This component is reused across
  // navigation, so on a change we reset the bookkeeping above — otherwise the
  // previous chapter's ribbons would fade out at stale positions or be matched
  // against this chapter's.
  const chapterIdRef = useRef("");
  const signatureRef = useRef("");

  // Drop a ribbon after its fade-out.
  const removeRibbon = (key: string) => {
    exitTimers.current.delete(key);
    renderedRef.current = renderedRef.current.filter((r) => r.key !== key);
    setRibbons(renderedRef.current);
  };

  // Measure the highlighted runs' live text geometry and turn each into a
  // rounded ribbon path drawn behind the text by the SVG layer. Runs after
  // every render (highlights/chapter/settings changes re-render this component)
  // and on reflow via the ResizeObserver below. The signature guard keeps the
  // measure -> setState -> re-render cycle from looping.
  const measureRibbons = () => {
    const content = contentRef.current;
    if (!content) return;

    // This component is reused as the reader navigates. When the chapter changes,
    // drop the previous chapter's ribbon bookkeeping so its ribbons don't fade
    // out at stale positions or get matched against this chapter's runs.
    const chapter = chapterData.value;
    const chapterId = chapter
      ? `${chapter.translation.id}:${chapter.book.id}:${chapter.chapter.number}`
      : "";
    if (chapterId !== chapterIdRef.current) {
      chapterIdRef.current = chapterId;
      renderedRef.current = [];
      prevCoverageRef.current = new Set();
      signatureRef.current = "";
      exitTimers.current.forEach((id) => clearTimeout(id));
      exitTimers.current.clear();
    }

    const box = content.getBoundingClientRect();
    const style = getComputedStyle(content);
    const fontSize = parseFloat(style.fontSize) || 16;
    const radius = RIBBON_RADIUS_EM * fontSize;
    const padX = RIBBON_PAD_X_EM * fontSize;
    // Line pitch (slot height) so a run's outer edges fill their line slots and
    // adjacent ribbons meet with no leading gap. Only used for single-line runs;
    // multi-line runs derive the pitch from their measured lines. Guard against a
    // non-px / "normal" computed line-height by falling back to ~1.5em.
    const computedPitch = parseFloat(style.lineHeight);
    const linePitch = computedPitch > fontSize ? computedPitch : fontSize * 1.5;
    const rtl = style.direction === "rtl";

    // Phase 1: measure every highlighted run's per-line geometry. `leadPad` /
    // `trailPad` default to padX and may be dropped to 0 below where the run's
    // edge sits alongside another verse's text on the same line.
    const runs = Array.from(
      content.querySelectorAll<HTMLElement>("[data-highlight-fill]")
    )
      .map((el, index) => ({
        el,
        key: el.getAttribute("data-highlight-key") || `i${index}`,
        fill: el.getAttribute("data-highlight-fill") ?? "",
        broadcast: el.getAttribute("data-highlight-broadcast") === "true",
        lines: collectLineRects(el, box.left, box.top),
        leadPad: padX,
        trailPad: padX,
      }))
      .filter((run) => run.fill !== "" && run.lines.length > 0);

    // Phase 2: where a run begins or ends mid-line — with another verse's text
    // right beside it on the same visual line — its horizontal pad would reach
    // over into that text. This happens whenever a highlighted verse starts (or
    // ends) partway along a line, whether the neighbour is a plain unhighlighted
    // verse ("...had your fill. ²⁷Do not work...") or a differently-colored
    // highlight abutting on the same line. Drop the pad on just that facing edge
    // so the ribbon stops at its own glyphs and leaves the verse-number margin as
    // a clean gutter. Every edge that faces a line break or the page margin keeps
    // its pad. `adjacentInlineRect` reports the neighbouring text on each side;
    // lead/trail map to the correct physical edge for RTL inside buildRibbonPath.
    const sharesLine = (r: RibbonRect, line: RibbonRect) =>
      r.top < line.bottom - 2 && r.bottom > line.top + 2;
    for (const run of runs) {
      const first = run.lines[0]!;
      const last = run.lines[run.lines.length - 1]!;
      const before = adjacentInlineRect(run.el, "before", box.left, box.top);
      const after = adjacentInlineRect(run.el, "after", box.left, box.top);
      if (before && sharesLine(before, first)) run.leadPad = 0;
      if (after && sharesLine(after, last)) run.trailPad = 0;
    }

    const next: Array<{
      key: string;
      d: string;
      fill: string;
      broadcast: boolean;
      first: number;
      last: number;
    }> = [];
    for (const run of runs) {
      const d = buildRibbonPath(run.lines, radius, padX, linePitch, {
        leadPad: run.leadPad,
        trailPad: run.trailPad,
        rtl,
      });
      if (!d) continue;
      // Split the "5-8" range back to numbers for the coverage checks below, and
      // prefix the chapter so keys never collide across chapters.
      const dash = run.key.indexOf("-");
      const first = dash >= 0 ? Number(run.key.slice(0, dash)) : NaN;
      const last = dash >= 0 ? Number(run.key.slice(dash + 1)) : NaN;
      next.push({
        key: `${chapterId}:${run.key}`,
        d,
        fill: run.fill,
        broadcast: run.broadcast,
        first,
        last,
      });
    }

    const signature = JSON.stringify(next);
    if (signature === signatureRef.current) return;
    signatureRef.current = signature;

    // Reconcile with what's on screen. A run fades in only if none of its verses
    // were highlighted before, and fades out only if none are highlighted now;
    // otherwise it just reshaped (edit/reflow) -> snap.
    const liveCoverage = new Set<number>();
    for (const r of next) {
      for (let v = r.first; v <= r.last; v++) liveCoverage.add(v);
    }
    const prevCoverage = prevCoverageRef.current;
    const prevLiveKeys = new Set(
      renderedRef.current.filter((p) => !p.exiting).map((p) => p.key)
    );
    const liveKeys = new Set(next.map((r) => r.key));

    const result: Ribbon[] = next.map((r) => {
      const timer = exitTimers.current.get(r.key);
      if (timer !== undefined) {
        // Re-highlighted mid-fade — cancel its removal.
        clearTimeout(timer);
        exitTimers.current.delete(r.key);
      }
      // New key + no verse highlighted before = genuinely new -> fade in; a
      // reused key or already-highlighted verses (reshape) snaps.
      let enter = !prevLiveKeys.has(r.key);
      if (enter) {
        for (let v = r.first; v <= r.last; v++) {
          if (prevCoverage.has(v)) {
            enter = false;
            break;
          }
        }
      }
      return {
        key: r.key,
        d: r.d,
        fill: r.fill,
        broadcast: r.broadcast,
        first: r.first,
        last: r.last,
        enter,
        exiting: false,
      };
    });

    for (const prev of renderedRef.current) {
      if (liveKeys.has(prev.key)) continue;
      if (prev.exiting) {
        // Already fading out — keep it until its timer fires.
        if (exitTimers.current.has(prev.key)) result.push(prev);
        continue;
      }
      // Verses still highlighted elsewhere = reshaped/merged -> drop now, no
      // fade; otherwise it's a real removal -> fade out.
      let stillCovered = false;
      for (let v = prev.first; v <= prev.last; v++) {
        if (liveCoverage.has(v)) {
          stillCovered = true;
          break;
        }
      }
      if (stillCovered) continue;
      result.push({ ...prev, enter: false, exiting: true });
      const key = prev.key;
      exitTimers.current.set(
        key,
        window.setTimeout(() => removeRibbon(key), RIBBON_FADE_MS + 50)
      );
    }

    renderedRef.current = result;
    prevCoverageRef.current = liveCoverage;
    setRibbons(result);
  };

  const chapterKey = currentChapter
    ? `${currentChapter.translation.id}:${currentChapter.book.id}:${currentChapter.chapter.number}`
    : "";

  // What this reader can see, reported as it scrolls. An IntersectionObserver
  // rather than a scroll listener: it only wakes when a verse crosses the edge
  // of the viewport, so a scroll doesn't measure every verse on every frame.
  // Re-armed per chapter, because the verse elements are replaced wholesale.
  //
  // Which verses are on screen is tracked per element rather than per verse
  // number, and the observed set is re-synced after every render (through
  // `syncVersesRef`, called from the layout effect below). Highlighting a
  // verse re-parents its span into a run wrapper, so Preact swaps the element
  // out: the detached one reports that it left the screen while its
  // replacement is not observed at all. Keyed by number that dropped every
  // highlighted verse from the range, so a reader looking at verses 1-19 with
  // 1-10 highlighted told its peers it was on 11-19.
  const syncVersesRef = useRef<() => void>(() => {});
  useEffect(() => {
    const content = contentRef.current;
    if (
      !content ||
      !onVisibleVersesChange ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }

    const visible = new Map<Element, number>();
    const observed = new Set<Element>();
    const report = () => {
      let first = Infinity;
      let last = -Infinity;
      for (const verseNumber of visible.values()) {
        if (verseNumber < first) first = verseNumber;
        if (verseNumber > last) last = verseNumber;
      }
      onVisibleVersesChange(first === Infinity ? null : { first, last });
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const el = entry.target as HTMLElement;
        const verseNumber = Number(el.dataset.verseNumber ?? NaN);
        if (
          entry.isIntersecting &&
          el.isConnected &&
          Number.isFinite(verseNumber)
        ) {
          visible.set(el, verseNumber);
        } else {
          visible.delete(el);
        }
      }
      report();
    });

    const sync = () => {
      let dropped = false;
      for (const el of observed) {
        if (el.isConnected) continue;
        observer.unobserve(el);
        observed.delete(el);
        dropped = visible.delete(el) || dropped;
      }
      for (const el of content.querySelectorAll<HTMLElement>(
        ".sb-verse[data-verse-number]"
      )) {
        if (observed.has(el)) continue;
        observed.add(el);
        observer.observe(el);
      }
      // Every newly observed element gets an entry from the observer, which
      // reports on their behalf. A verse that only went away does not.
      if (dropped) report();
    };
    syncVersesRef.current = sync;
    sync();

    return () => {
      syncVersesRef.current = () => {};
      observer.disconnect();
      onVisibleVersesChange(null);
    };
  }, [chapterKey, onVisibleVersesChange]);

  const [presenceMarkers, setPresenceMarkers] = useState<PresenceMarker[]>([]);
  // Signature of the last markers written to state, so the measure -> setState
  // -> re-render -> measure cycle settles instead of looping.
  const presenceSignatureRef = useRef("");

  // A participant's bar spans from the top of the first verse they can see to
  // the bottom of the last, measured from the live line boxes so it follows
  // the text however it wraps.
  const measurePresence = () => {
    const content = contentRef.current;
    if (!content) return;

    const next: PresenceMarker[] = [];
    if (presence && presence.length > 0) {
      const box = content.getBoundingClientRect();
      for (const participant of presence) {
        const firstEl = content.querySelector<HTMLElement>(
          `.sb-verse[data-verse-number="${participant.firstVerse}"]`
        );
        const lastEl = content.querySelector<HTMLElement>(
          `.sb-verse[data-verse-number="${participant.lastVerse}"]`
        );
        const firstRects = firstEl?.getClientRects();
        const lastRects = lastEl?.getClientRects();
        const firstRect = firstRects?.[0];
        const lastRect = lastRects?.[lastRects.length - 1];
        if (!firstRect || !lastRect) continue;
        const top = firstRect.top - box.top;
        const bottom = lastRect.bottom - box.top;
        if (bottom <= top) continue;
        next.push({
          connectionId: participant.connectionId,
          displayName: participant.displayName,
          imageUrl: participant.imageUrl,
          visual: participant.visual,
          top,
          height: bottom - top,
          lane: 0,
        });
      }
    }

    // Deal the bars into lanes so people reading the same verses stand beside
    // each other instead of hiding one another. Taking them top-down and
    // putting each in the first lane whose previous occupant has already ended
    // uses no more lanes than there are people genuinely overlapping at once,
    // so a session spread through a chapter keeps everyone on the centre line.
    // Ties break on connection id to keep a lane from changing hands between
    // two bars that start on the same line.
    next.sort(
      (a, b) => a.top - b.top || a.connectionId.localeCompare(b.connectionId)
    );
    const laneBottoms: number[] = [];
    for (const marker of next) {
      const bottom = marker.top + marker.height;
      const free = laneBottoms.findIndex((end) => marker.top >= end);
      const lane = free === -1 ? laneBottoms.length : free;
      laneBottoms[lane] = Math.max(laneBottoms[lane] ?? 0, bottom);
      marker.lane = lane;
    }

    const signature = next
      .map(
        (m) =>
          `${m.connectionId}:${Math.round(m.top)}:${Math.round(m.height)}:${m.lane}:${m.imageUrl ?? ""}`
      )
      .join("|");
    if (signature === presenceSignatureRef.current) return;
    presenceSignatureRef.current = signature;
    setPresenceMarkers(next);
  };

  // The verse each note starts at, deduplicated: two notes on the same verse
  // get one marker, and a note spanning 3-6 marks verse 3 only.
  const noteVerseNumbers = Array.from(
    new Set(
      chapterAnnotations
        .map((annotation) => {
          const verses = annotationVerseNumbers(annotation);
          return verses.length > 0 ? Math.min(...verses) : null;
        })
        .filter((verseNumber): verseNumber is number => verseNumber !== null)
    )
  ).sort((a, b) => a - b);
  // Open on every mobile chapter, notes or not: the gutter's indent narrows
  // the text, so opening it only where there are notes would reflow the page
  // each time the reader flips between an annotated chapter and a bare one.
  const showNoteGutter = isMobile;

  const [noteMarkers, setNoteMarkers] = useState<NoteMarker[]>([]);
  // Signature of the last markers written to state, so the measure -> setState
  // -> re-render -> measure cycle settles instead of looping.
  const noteMarkerSignatureRef = useRef("");

  // Where each note marker sits vertically. Markers live in a gutter beside
  // the text rather than in the flow, so their position has to be measured:
  // it is the verse's *first* visual line box, which is the line carrying
  // the verse number.
  const measureNoteMarkers = () => {
    const content = contentRef.current;
    if (!content) return;

    const next: NoteMarker[] = [];
    if (showNoteGutter) {
      const box = content.getBoundingClientRect();
      for (const verseNumber of noteVerseNumbers) {
        const verseEl = content.querySelector<HTMLElement>(
          `.sb-verse[data-verse-number="${verseNumber}"]`
        );
        if (!verseEl) continue;
        // A verse containing poetry is block-level (so is each of its lines),
        // which makes its own client rect the whole multi-line column —
        // centring on that drops the marker halfway down the verse.
        // `collectLineRects` walks past the block lines and reports one rect
        // per *visual* line, so [0] is the line carrying the verse number
        // whether the verse is prose or poetry.
        const line = collectLineRects(verseEl, box.left, box.top)[0];
        if (!line) continue;
        next.push({
          verseNumber,
          top: line.top,
          height: line.bottom - line.top,
        });
      }
    }

    const signature = next
      .map(
        (m) => `${m.verseNumber}:${Math.round(m.top)}:${Math.round(m.height)}`
      )
      .join("|");
    if (signature === noteMarkerSignatureRef.current) return;
    noteMarkerSignatureRef.current = signature;
    setNoteMarkers(next);
  };

  // The measurements re-run after every render, and the ResizeObserver below
  // reaches them through this ref instead of closing over them. Registered
  // once at mount, that callback kept the very first render's `presence` —
  // the empty list a reader has before any peer position arrives — so every
  // reflow measured "nobody here", wiped the markers, and closed the gutter,
  // which moved the text out from under the highlight ribbons that had just
  // been measured against it.
  const remeasureRef = useRef<() => void>(() => {});

  useLayoutEffect(() => {
    remeasureRef.current = () => {
      measureRibbons();
      measurePresence();
      measureNoteMarkers();
    };
    measureRibbons();
    measurePresence();
    measureNoteMarkers();
    syncVersesRef.current();
  });

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => remeasureRef.current());
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  // Clear pending fade-out timers on unmount.
  useLayoutEffect(() => {
    const timers = exitTimers.current;
    return () => {
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
    };
  }, []);

  if (chapterData.value === null) {
    if (!initialChapterLoadSettled.value) {
      throw chapterDataPromise;
    }
    // The load finished and produced nothing. Rendering the error branch above
    // is the caller's job; throwing the (now-resolved) promise again would just
    // suspend and resume forever.
    return null;
  }

  const containerClasses = decorations.value
    .filter(
      (d) =>
        d.containerClassName &&
        d.bookId === chapterData.value?.book.id &&
        d.chapterNumber === chapterData.value?.chapter.number
    )
    .map((d) => d.containerClassName)
    .join(" ");

  // verse number -> full ChapterVerse, so a gutter marker can select its verse
  // the same way tapping the verse itself does.
  const verseByNumber = new Map<number, ChapterVerse>();
  for (const entry of chapterData.value.chapter.content) {
    if (
      entry &&
      typeof entry === "object" &&
      entry.type === "verse" &&
      typeof entry.number === "number"
    ) {
      verseByNumber.set(entry.number, entry as ChapterVerse);
    }
  }

  return (
    <div
      ref={contentRef}
      className={`sb-chapter-content${
        props.isStale ? " sb-chapter-content-stale" : ""
      }${presence !== undefined ? " sb-chapter-content-presence" : ""}${
        showNoteGutter ? " sb-chapter-content-note-gutter" : ""
      } ${containerClasses}`}
      onPointerDown={() => {
        justConvertedSelectionRef.current = false;
      }}
      onPointerUp={selectVersesFromTextSelection}
    >
      <svg className="sb-highlight-layer" aria-hidden="true">
        {ribbons.map((ribbon) => (
          <path
            key={ribbon.key}
            className={[
              "sb-highlight-ribbon",
              ribbon.enter ? "sb-highlight-ribbon-enter" : "",
              ribbon.broadcast ? "sb-highlight-ribbon-broadcast" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            d={ribbon.d}
            style={{
              fill: ribbon.fill,
              // Same colour on the stroke; the class decides how much of the
              // fill shows through.
              stroke: ribbon.broadcast ? ribbon.fill : undefined,
              opacity: ribbon.exiting ? 0 : undefined,
            }}
          />
        ))}
      </svg>
      {showNoteGutter && (
        <div className="sb-note-gutter" aria-hidden={noteMarkers.length === 0}>
          {noteMarkers.map((marker) => (
            <button
              key={marker.verseNumber}
              type="button"
              className="sb-note-gutter-marker"
              style={{ top: `${marker.top}px`, height: `${marker.height}px` }}
              aria-label={t("notes-for-verse", {
                verse: marker.verseNumber,
                defaultValue: "Notes for verse {{verse}}",
              })}
              onClick={(event: MouseEvent) => {
                const value = verseByNumber.get(marker.verseNumber);
                if (!value || !chapterData.value) return;
                onAnnotationVerseClick(
                  {
                    bookId: chapterData.value.book.id,
                    chapterNumber: chapterData.value.chapter.number,
                    verse: value,
                    translationId: chapterData.value.translation.id,
                  },
                  marker.verseNumber,
                  event
                );
              }}
            >
              <span className="material-symbols-outlined">sticky_note_2</span>
            </button>
          ))}
        </div>
      )}
      {presence !== undefined && (
        <PresenceGutter
          markers={presenceMarkers}
          chapterKey={chapterKey}
          contentRef={contentRef}
        />
      )}
      {renderChapterContent(
        chapterData.value,
        (verse, event) => {
          // Swallow the click that trails a drag-to-select gesture so it
          // doesn't toggle the just-selected verse back off.
          if (justConvertedSelectionRef.current) {
            justConvertedSelectionRef.current = false;
            return;
          }
          selectVerse(verse, event.clientX, event.clientY);
        },
        selectedVerses.value,
        (noteId) => selectFootnote(noteId),
        highlights.value.highlights,
        decorations.value,
        chapterAnnotations,
        scriptureElements,
        onAnnotationVerseClick
      )}
    </div>
  );
}

/**
 * A translation's metadata, from the reader's own book list when that's the
 * one asked for, otherwise from the first catalog that has it. The book list
 * alone isn't enough: it's missing when a translation never opened fails
 * offline, and it belongs to the translation on screen, not one a failed
 * switch was headed for.
 */
function resolveTranslation(
  translationId: string,
  fromBookList: Translation | null,
  ...catalogs: Array<readonly Translation[] | null | undefined>
): Translation | null {
  if (fromBookList?.id === translationId) {
    return fromBookList;
  }
  for (const catalog of catalogs) {
    const entry = catalog?.find((candidate) => candidate.id === translationId);
    if (entry) {
      return entry;
    }
  }
  return null;
}

export function BibleReader(props: BibleReaderProps) {
  const {
    currentSlot,
    readingState,
    selectorState,
    state,
    mobileChrome,
    sharedSession,
  } = props;
  const {
    translationId,
    translation,
    bookId,
    chapterNumber,
    availableTranslations,
    translationBooks,
    chapterData,
    selectedVerses,
    highlights,
    decorations,
    loading,
    isChapterContentStale,
    error,
    failedTranslationId,
    selectVerse,
    clearSelectedVerses,
    selectedFootnote,
    selectFootnote,
  } = readingState;
  const footnoteOverlayDismiss = useOverlayDismiss(() => selectFootnote(null));

  if (import.meta.env.SSR && !readingState.initialChapterLoadSettled.value) {
    throw readingState.chapterDataPromise;
  }

  const currentBook = computed(
    () =>
      translationBooks.value?.books.find((book) => book.id === bookId.value) ??
      null
  );
  // The requested book wasn't found in this translation's book list — a
  // genuinely unrecognized book/name, or one absent from this specific
  // translation. `loadInitialData` deliberately stops rather than silently
  // substituting a different book's content once loading settles.
  const bookNotFound = computed(
    () =>
      !loading.value &&
      !error.value &&
      translationBooks.value !== null &&
      bookId.value !== null &&
      currentBook.value === null
  );
  // Display name for the header/title: the catalog entry's name, falling
  // back to the loaded chapter's own book record while the catalog is still
  // in flight. The book catalog and the chapter content load independently
  // (see `loadInitialData`'s comment on the raw position signals firing the
  // content effect before the catalog-backed check completes), so — same as
  // `SeedBibleStateManager`'s `resolveCurrentBook` for the document title —
  // `currentBook` can still be null here even after SSR has suspended on
  // (and resolved) `chapterDataPromise`. Without this, the header would show
  // the raw book id ("GEN") instead of its name whenever that race lands the
  // chapter first.
  const currentBookName = computed(
    () => currentBook.value?.name ?? chapterData.value?.book.name ?? null
  );
  const translationLicenseNotice = computed(
    () => translation.value?.licenseNotice?.trim() ?? ""
  );
  const translationWebsite = computed(
    () => translation.value?.website.trim() ?? ""
  );

  const isMinimalEmbed = state?.app.isMinimalEmbed?.value ?? false;
  const isCompactReader =
    state?.app.isCompactReader?.value ?? state?.app.isMobile.value ?? false;

  // Where the other people in this session are (#1692). Only participants
  // reading the same chapter can be placed against verses on this page, and
  // this reader is never its own marker. Outside a session no gutter is drawn
  // at all (the list is withheld from ChapterContent below).
  //
  // Built in the render body rather than in a `useComputed`: the session
  // arrives as a prop, and a memoised computed that returns early on a null one
  // subscribes to no signal at all, which leaves it permanently un-dirtied. A
  // reader that first rendered outside a session and was then handed one (a
  // session tab replacing a plain tab in the same slot reuses the component)
  // would keep answering "nobody" for the rest of its life. Reading the signals
  // here subscribes this component to them directly, which survives that prop
  // change.
  const presence: ParticipantPresence[] = [];
  const presenceBookId = bookId.value;
  const presenceChapterNumber = chapterNumber.value;
  if (sharedSession && presenceBookId && presenceChapterNumber > 0) {
    const positions = sharedSession.participantPositions.value;
    for (const user of sharedSession.connectedUsers.value) {
      if (user.isSelf) continue;
      const position = positions.get(user.connectionId);
      if (
        !position ||
        position.bookId !== presenceBookId ||
        position.chapterNumber !== presenceChapterNumber ||
        position.firstVerse === undefined ||
        position.lastVerse === undefined
      ) {
        continue;
      }
      presence.push({
        connectionId: user.connectionId,
        displayName: getUserDisplayName(user),
        imageUrl: user.profile?.pictureUrl ?? null,
        visual: user.visual,
        firstVerse: position.firstVerse,
        lastVerse: position.lastVerse,
      });
    }
  }

  // Stable identity so the observer in ChapterContent isn't torn down and
  // rebuilt on every render of the reader.
  const reportVisibleVerses = useCallback(
    (range: VisibleVerseRange | null) => {
      const current = readingState.visibleVerseRange.peek();
      if (current?.first === range?.first && current?.last === range?.last) {
        return;
      }
      readingState.visibleVerseRange.value = range;
    },
    [readingState]
  );

  // Clicking an annotated verse number jumps straight to its note: on
  // mobile, it also selects the verse (like clicking its text does) and
  // expands/scrolls to the note in the mobile verse toolbar. On desktop,
  // where that toolbar isn't used, it leaves the verse selection alone and
  // just forces the compact discover panel beside the scripture text and
  // scrolls/highlights the note there.
  const handleAnnotationVerseClick = (
    verse: BibleSelectedVerse,
    verseNumber: number,
    event: MouseEvent
  ) => {
    // The <sup> sits inside the verse's own clickable <span>; stop the tap
    // here so selectVerse (a toggle) doesn't run twice and immediately undo
    // itself.
    event.stopPropagation();
    if (!state) {
      return;
    }

    if (isCompactReader) {
      selectVerse(verse, event.clientX, event.clientY);
      readingState.pendingAnnotationScrollVerse.value = verseNumber;
      return;
    }

    if (state.settings.settings.value.discoveredContent.showContent) {
      readingState.discoverContentPanelInline.value = true;
    } else {
      state.discover.view.value = "discover";
    }

    // AnnotationsSection's shared effect reacts to this target either way —
    // scrolling to and highlighting the note's group — whether it's mounted
    // in this tab's compact panel or the toolbar-toggled Discover pane.
    state.discover.scrollToVerse.value = {
      bookId: verse.bookId,
      chapterNumber: verse.chapterNumber,
      verseNumber,
    };
  };

  // Reader glyph size is its own knob, independent of the UI-scale (`rem`)
  // system. Anchoring `.sb-font-size-*` here (rather than on the chrome root)
  // keeps `.sb-chapter-content { font-size: 1em }` and reader-`em` spacing
  // tied to the reader setting, while chrome inherits the UI scale from `html`.
  const readerFontSizeClass = `sb-font-size-${(
    state?.settings?.settings.value.fontSize ?? "M"
  ).toLowerCase()}`;

  // Hard-gated off under SSR, which is what keeps both the dimming and the
  // placeholder out of the served HTML. Rendering the placeholder server-side
  // would strip the scripture out of the document — for a Bible reader that is
  // an SEO regression, not a cosmetic one. The reader suspends on
  // `chapterDataPromise` there instead, so by render time there is either
  // content or a settled failure.
  const isContentStale = !import.meta.env.SSR && isChapterContentStale.value;
  // Held back by `CHAPTER_SKELETON_DELAY_MS` so a fast navigation shows only
  // dimmed text, never a flash of placeholder. Skipped when there is no chapter
  // on screen to dim — a cold start would otherwise sit blank for the delay.
  const [isWaitLong, setIsWaitLong] = useState(false);
  useEffect(() => {
    if (!isContentStale) {
      setIsWaitLong(false);
      return;
    }
    const timer = window.setTimeout(
      () => setIsWaitLong(true),
      CHAPTER_SKELETON_DELAY_MS
    );
    return () => window.clearTimeout(timer);
  }, [isContentStale]);

  const showChapterSkeleton =
    isContentStale && (chapterData.value === null || isWaitLong);
  const dimStaleChapter = isContentStale && !showChapterSkeleton;

  const { t, language } = useI18n();
  const scriptureElements: ScriptureElementsBehavior =
    props.scriptureElements ??
      state?.settings?.settings.value.scriptureElements ?? {
        showHeadings: true,
        showVerseNumbers: true,
        showFootnotes: true,
        showHighlights: true,
        showRedLettering: true,
      };

  const openBookSelector = () => {
    selectorState.selectingTranslation.value = false;
    void selectorState.setOpen(true, currentSlot);
  };
  const openTranslationSelector = async () => {
    await selectorState.setOpen(true, currentSlot);
    selectorState.selectingTranslation.value = true;
  };

  // The translation chip, in both the desktop and the mobile header: the short
  // name to read, the full name to announce.
  const translationLabel =
    translation.value?.shortName ?? translationId.value ?? "";
  const changeTranslationLabel = t("change-translation", {
    defaultValue: "Change translation ({{name}})",
    name: translation.value?.name ?? translationLabel,
  });

  // True for the click that trails a drag-to-select gesture, so the verse's
  // own onClick doesn't toggle the verse back off after we've just selected
  // it from the text selection. Reset at the start of every new gesture.
  const justConvertedSelectionRef = useRef(false);

  // Turn a native text selection (mouse drag on desktop, touch text-selection
  // on mobile) into an app verse selection: select every verse the selection
  // touches — exactly as if the user had clicked each of them — which opens
  // the verse toolbar. No-op for a collapsed/empty selection, so plain taps
  // keep their single-verse toggle behaviour.
  const selectVersesFromTextSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return;
    }
    const data = chapterData.value;
    if (!data) return;

    const range = selection.getRangeAt(0);
    const ancestor =
      range.commonAncestorContainer instanceof Element
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentElement;
    const root = ancestor?.closest(".sb-chapter-content");
    if (!root) return;

    const verseEls = Array.from(
      root.querySelectorAll<HTMLElement>(".sb-verse[data-verse-number]")
    ).filter((el) => range.intersectsNode(el));
    if (verseEls.length === 0) return;

    // verse number -> full ChapterVerse, so we can rebuild selection entries.
    const verseByNumber = new Map<number, ChapterVerse>();
    for (const entry of data.chapter.content) {
      if (
        entry &&
        typeof entry === "object" &&
        entry.type === "verse" &&
        typeof entry.number === "number"
      ) {
        verseByNumber.set(entry.number, entry as ChapterVerse);
      }
    }

    // Anchor the floating verse toolbar near the selected text.
    const rect = range.getBoundingClientRect();
    const anchorX = rect.left + rect.width / 2;
    const anchorY = rect.top;

    // Drop the native selection so only the app's verse highlight shows and
    // the trailing click can't toggle a verse back off.
    selection.removeAllRanges();
    justConvertedSelectionRef.current = true;

    // Mirror clicking each covered verse: deselect everything, then reselect.
    clearSelectedVerses();
    for (const el of verseEls) {
      const verseValue = verseByNumber.get(Number(el.dataset.verseNumber));
      if (!verseValue) continue;
      selectVerse(
        {
          bookId: data.book.id,
          chapterNumber: data.chapter.number,
          verse: verseValue,
          translationId: data.translation.id,
        },
        anchorX,
        anchorY
      );
    }
  };

  // Only the current panel's title opens the selector; the side panels are
  // non-interactive previews for the swipe transition.
  const renderMobileChapterTitle = (
    bookName: string,
    chapter: number | string,
    interactive = false
  ) => (
    <h2
      className={`sb-bible-reader-mobile-content-title${
        interactive ? " sb-bible-reader-mobile-content-title-tappable" : ""
      }`}
      {...(interactive ? flingSafeTapHandlers(openBookSelector) : {})}
    >
      <span className="sb-bible-reader-book">{bookName}</span>
      <span className="sb-bible-reader-chapter">{chapter}</span>
    </h2>
  );

  /**
   * Placeholder shown in place of the verses while the chapter the reader has
   * navigated to is still downloading. Without it, a fast skim shows the *old*
   * chapter's text under the new chapter's title, which reads as though the
   * navigation silently failed.
   */
  const renderChapterSkeleton = () => (
    <SkeletonContainer
      label={t("loading-chapter", { defaultValue: "Loading chapter…" })}
      className={`sb-chapter-content sb-chapter-skeleton${
        isCompactReader ? " sb-chapter-content-note-gutter" : ""
      }`}
    >
      <Skeleton shape="block" width="42%" />
      {CHAPTER_SKELETON_PARAGRAPHS.map((widths, paragraph) => (
        <div className="sb-chapter-skeleton-paragraph" key={paragraph}>
          {widths.map((width, line) => (
            <Skeleton shape="line" key={line} width={width} />
          ))}
        </div>
      ))}
    </SkeletonContainer>
  );

  // Keep the failure state on screen while a retry is in flight — `retryLoad()`
  // clears `error` as it starts, so without this the panel would flash back to
  // the (still empty) chapter body before the new request settles.
  //
  // Holds the translation being retried rather than a flag: `error` (and with
  // it `failedTranslationId`) is cleared for the length of the retry, and the
  // panel still has to say which translation failed.
  const [retryingTranslationId, setRetryingTranslationId] = useState<
    string | null
  >(null);
  const retrying = retryingTranslationId !== null;
  const offline = state?.bibleData?.offline;
  const retryChapterLoad = async () => {
    if (retrying) return;
    const attemptedTranslationId =
      failedTranslationId.peek() ?? translationId.peek();
    setRetryingTranslationId(attemptedTranslationId);
    try {
      await readingState.retryLoad();
    } finally {
      setRetryingTranslationId(null);
    }
    // A recovery only if the reader ended up on the translation that failed:
    // retrying a failed switch lands there, but a reader who moved somewhere
    // else mid-retry is looking at a chapter that never failed to load.
    if (
      !offline ||
      error.peek() ||
      translationId.peek() !== attemptedTranslationId
    ) {
      return;
    }
    const recoveredTranslation = resolveTranslation(
      attemptedTranslationId,
      translation.peek(),
      availableTranslations.peek()?.translations,
      state?.bibleData?.availableTranslations?.peek()
    );
    if (recoveredTranslation) {
      offline.offerRecoveryPrompt(recoveredTranslation);
    }
  };
  const showLoadError = (!!error.value && !loading.value) || retrying;

  const failedId =
    retryingTranslationId ?? failedTranslationId.value ?? translationId.value;
  // The app-wide catalog too: the reader's own copy is only filled in by some
  // load paths, and a switch picked from the selector's list may be the first
  // this reader has heard of that translation.
  const failedTranslation = resolveTranslation(
    failedId,
    translation.value,
    availableTranslations.value?.translations,
    state?.bibleData?.availableTranslations?.value
  );
  const failedTranslationNotSaved =
    !!offline?.supported &&
    !!failedTranslation &&
    !offline.isDownloaded(failedTranslation.id);
  const hasOtherSavedTranslation =
    !!offline?.supported &&
    [...offline.downloaded.value.keys()].some(
      (savedId) => savedId !== failedId
    );

  const offlineRecords = state?.bibleData?.offline?.records.value;
  // The signal, not `getCachedTranslationBooks`: that helper reads untracked,
  // so a catalog that arrives after this panel is on screen would never
  // update the offer. Downloaded copies carry their own book list too; the
  // cache wins when both exist, because that is the catalog a switch would use.
  const cachedTranslationBooks = state?.bibleData?.translationBooks?.value;
  const requestedTranslationLanguage =
    translation.value?.language ||
    availableTranslations.value?.translations.find(
      (item) => item.id === translationId.value
    )?.language ||
    (translationId.value
      ? offlineRecords?.get(translationId.value)?.translation.language
      : undefined);
  const offlineFallbackTranslations =
    showLoadError && offlineRecords
      ? findOfflineTranslationFallbacks({
          currentTranslationId: translationId.value,
          currentTranslationLanguage: requestedTranslationLanguage,
          uiLanguage: language,
          downloaded: Array.from(offlineRecords.values()),
          bookId: bookId.value,
          chapterNumber: chapterNumber.value ?? 1,
          booksFor: (id) =>
            cachedTranslationBooks?.get(id)?.books ??
            offlineRecords.get(id)?.books ??
            null,
        })
      : [];

  const switchToOfflineTranslation = (nextTranslation: Translation) => {
    const book = bookId.value;
    const chapter = chapterNumber.value ?? 1;
    if (!book) {
      void readingState.selectTranslation(nextTranslation.id);
      return;
    }
    void readingState.selectTranslationAndChapter(
      nextTranslation.id,
      book,
      chapter
    );
  };

  const renderMainContent = () => (
    <>
      {isCompactReader &&
        renderMobileChapterTitle(
          currentBookName.value ?? bookId.value ?? "",
          chapterNumber.value ?? "",
          true
        )}

      {bookNotFound.value && (
        <div className="sb-reader-not-found">
          <span
            className="material-symbols-outlined sb-reader-not-found-icon"
            aria-hidden="true"
          >
            search_off
          </span>
          <p className="sb-reader-not-found-title">
            {t("book-not-found-title", { defaultValue: "Book not found" })}
          </p>
          <p className="sb-reader-not-found-body">
            {t("book-not-found-message", {
              defaultValue:
                "We couldn't find that book in {{translationName}}.",
              translationName:
                translation.value?.name ?? translationId.value ?? "",
            })}
          </p>
          {translationBooks.value?.books[0] && (
            <button
              type="button"
              className="sb-reader-not-found-action"
              onClick={() => {
                const firstBook = translationBooks.value!.books[0]!;
                void readingState.selectChapter(
                  firstBook.id,
                  firstBook.firstChapterNumber ?? 1
                );
              }}
            >
              {t("book-not-found-action", {
                defaultValue: "Go to {{bookName}} {{chapterNumber}}",
                bookName: translationBooks.value.books[0].name,
                chapterNumber:
                  translationBooks.value.books[0].firstChapterNumber ?? 1,
              })}
            </button>
          )}
        </div>
      )}

      {showLoadError && (
        <div className="sb-reader-error" role="alert">
          <span
            className="material-symbols-outlined sb-reader-error-icon"
            aria-hidden="true"
          >
            cloud_off
          </span>
          <h2 className="sb-reader-error-title">
            {t("chapter-unavailable", { defaultValue: "Chapter unavailable" })}
          </h2>
          <p className="sb-reader-error-message">
            {t("chapter-unavailable-description", {
              defaultValue:
                "We were unable to load the data for this chapter. Please check your internet connection and try again.",
            })}
          </p>
          {offlineFallbackTranslations.length > 0 && (
            <p className="sb-reader-error-offline">
              {t("chapter-unavailable-offline-switch", {
                count: offlineFallbackTranslations.length,
                bookName: currentBookName.value ?? bookId.value ?? "",
                chapterNumber: chapterNumber.value ?? 1,
                defaultValue_one:
                  "You have a translation saved on your device that contains {{bookName}} {{chapterNumber}}.",
                defaultValue_other:
                  "You have {{count}} translations saved on your device that contain {{bookName}} {{chapterNumber}}.",
              })}
            </p>
          )}
          <div className="sb-reader-error-actions">
            <button
              type="button"
              className="sb-reader-error-retry"
              onClick={() => void retryChapterLoad()}
              disabled={retrying}
              aria-busy={retrying}
            >
              {retrying && <Spinner size="1.125rem" />}
              {t("reload", { defaultValue: "Reload" })}
            </button>
            {offlineFallbackTranslations.length > 0 && (
              <OfflineFallbackSwitch
                translations={offlineFallbackTranslations}
                onSwitch={switchToOfflineTranslation}
              />
            )}
          </div>

          {failedTranslationNotSaved && !offline.isOnline.value && (
            <p className="sb-reader-error-hint">
              {t("chapter-unavailable-download-offline-hint", {
                abbreviation: failedTranslation.shortName,
                defaultValue:
                  "Once you're back online, download {{abbreviation}} so this doesn't happen again.",
              })}
            </p>
          )}

          {hasOtherSavedTranslation &&
            offlineFallbackTranslations.length === 0 && (
              <div className="sb-reader-error-alternatives">
                <p className="sb-reader-error-hint">
                  {t("chapter-unavailable-switch-heading", {
                    defaultValue:
                      "Or keep reading in a translation saved on this device:",
                  })}
                </p>
                <button
                  type="button"
                  className="sb-reader-error-action"
                  onClick={() =>
                    void selectorState.openDownloadedTranslations(currentSlot)
                  }
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    translate
                  </span>
                  {t("chapter-unavailable-choose-saved", {
                    defaultValue: "Choose a saved translation",
                  })}
                </button>
              </div>
            )}
        </div>
      )}

      {!showLoadError &&
        !bookNotFound.value &&
        (showChapterSkeleton ? (
          renderChapterSkeleton()
        ) : (
          <Suspense
            fallback={
              <p>
                {t("no-chapter-content-found", {
                  defaultValue: "No chapter content found.",
                })}
              </p>
            }
          >
            <ChapterContent
              isStale={dimStaleChapter}
              chapterData={chapterData}
              chapterDataPromise={readingState.chapterDataPromise}
              initialChapterLoadSettled={readingState.initialChapterLoadSettled}
              selectedVerses={selectedVerses}
              selectVersesFromTextSelection={selectVersesFromTextSelection}
              justConvertedSelectionRef={justConvertedSelectionRef}
              highlights={highlights}
              decorations={decorations}
              annotations={state?.annotations}
              friends={state?.friends}
              selectVerse={selectVerse}
              selectFootnote={selectFootnote}
              scriptureElements={scriptureElements}
              onAnnotationVerseClick={handleAnnotationVerseClick}
              isMobile={isCompactReader}
              presence={sharedSession ? presence : undefined}
              onVisibleVersesChange={reportVisibleVerses}
            />
          </Suspense>
        ))}

      {!availableTranslations.value && !showLoadError && (
        <p>
          {t("no-translations-available", {
            defaultValue: "No translations available.",
          })}
        </p>
      )}

      {!showLoadError && translationLicenseNotice.value.length > 0 && (
        <>
          <p className="sb-translation-license-notice">
            {translationLicenseNotice.value}
          </p>
          {translationWebsite.value.length > 0 && (
            <p className="sb-translation-website">
              <a
                href={translationWebsite.value}
                target="_blank"
                rel="noopener noreferrer"
              >
                {translationWebsite.value}
              </a>
            </p>
          )}
        </>
      )}

      {/* Undefined on desktop, where the caller renders this itself below the
          reader — the desktop pane is its own scroll container. */}
      {mobileChrome?.belowContent}
    </>
  );

  // const extraContent = discoverPanel ? (
  //   <div className="sb-bible-reader-discover-panel">{discoverPanel}</div>
  // ) : null;
  const extraContent =
    state &&
    !state.app.isDiscoverOpen.value &&
    state.settings.settings.value.discoveredContent.showContent ? (
      <DiscoverContentPanel tab={currentSlot.tab} state={state} />
    ) : null;

  // Playback opens on the selected tab only. Other slots in a split view
  // must not show this loader.
  const playlistOpeningHere =
    !!state &&
    state.playlists.openingPlayback.value &&
    state.playlists.view.value !== "play_playlist" &&
    currentSlot.tab != null &&
    state.tabs.selectedTabId.value === currentSlot.tab.id;

  return (
    <div
      className={`sb-bible-reader ${readerFontSizeClass}${
        isCompactReader ? " sb-bible-reader-mobile" : ""
      }`}
      dir={translation.value?.textDirection ?? "auto"}
    >
      {playlistOpeningHere && !isCompactReader ? (
        <Spinner
          className="sb-playlist-opening-spinner"
          size="1rem"
          label={t("opening-playlist", { defaultValue: "Opening playlist" })}
        />
      ) : null}
      {isCompactReader && state ? (
        <Fragment key="mobile">
          <div
            className={`sb-bible-reader-mobile-header${
              !isMinimalEmbed && mobileChrome?.isScrolled
                ? " sb-bible-reader-mobile-header-hidden"
                : ""
            }`}
          >
            <div className="sb-bible-reader-mobile-header-text">
              <h1 className="sb-bible-reader-mobile-header-title">
                {!isMinimalEmbed && (
                  <span
                    className="sb-bible-reader-mobile-header-book"
                    onClick={openBookSelector}
                  >
                    {currentBookName.value ?? bookId.value ?? ""}{" "}
                    {chapterNumber.value}
                  </span>
                )}
                <button
                  type="button"
                  className="sb-bible-reader-mobile-header-translation"
                  aria-label={changeTranslationLabel}
                  onClick={(e: MouseEvent) => {
                    e.stopPropagation();
                    void openTranslationSelector();
                  }}
                >
                  {translationLabel}
                </button>
              </h1>
            </div>
            {playlistOpeningHere ? (
              <Spinner
                size="1rem"
                label={t("opening-playlist", {
                  defaultValue: "Opening playlist",
                })}
              />
            ) : null}
            {!isMinimalEmbed && (
              <ChapterNotesButton
                state={state}
                bookId={bookId.value}
                chapterNumber={chapterNumber.value}
              />
            )}
            <div className="sb-bible-reader-mobile-header-actions">
              {isMinimalEmbed ? (
                <>
                  <button
                    type="button"
                    className="sb-bible-reader-mobile-header-open-tab"
                    aria-label={t("open-in-new-tab", {
                      defaultValue: "Open in New Tab",
                    })}
                    title={t("open-in-new-tab", {
                      defaultValue: "Open in New Tab",
                    })}
                    onClick={() => {
                      window.open(
                        urlWithoutEmbedParam(state.navigation.currentUrl.value)
                          .href,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                  >
                    <MaterialIcon>open_in_new</MaterialIcon>
                  </button>
                  <button
                    type="button"
                    className="sb-bible-reader-mobile-header-settings"
                    onClick={() => mobileChrome?.onOpenMobileSettings()}
                    aria-label={t("settings", { defaultValue: "Settings" })}
                    title={t("settings", { defaultValue: "Settings" })}
                  >
                    <InfoSettingsIcon />
                  </button>
                </>
              ) : (
                <>
                  {!state.playlists.playing.value && (
                    <>
                      <ReaderSaveButton
                        state={state}
                        translationId={translationId.value}
                        bookId={bookId.value}
                        chapterNumber={chapterNumber.value}
                      />
                      <ReaderBookmarkButton
                        state={state}
                        translationId={translationId.value}
                        bookId={bookId.value}
                        chapterNumber={chapterNumber.value}
                      />
                    </>
                  )}
                  <QuickToolbar
                    toolsManager={state.tools}
                    readingState={readingState}
                    playlists={state.playlists}
                    annotations={state.annotations}
                    features={state.features}
                    settings={state.settings}
                    sharedSession={sharedSession ?? null}
                    toast={state.app.toast}
                    modals={state.modals}
                    app={state.app}
                    readingPlans={state.readingPlans}
                    className="sb-quick-toolbar-mobile-header"
                  />
                  {/*
                   * No account avatar here: "You" is a bottom-bar tab again
                   * (#1554), and two avatars on one screen made it unclear which
                   * one was the way to your profile.
                   */}
                  {sharedSession ? (
                    <MobileSessionParticipants
                      state={state}
                      session={sharedSession}
                    />
                  ) : null}
                  <button
                    type="button"
                    className="sb-bible-reader-mobile-header-settings"
                    onClick={() => mobileChrome?.onOpenMobileSettings()}
                    aria-label={t("settings", { defaultValue: "Settings" })}
                    title={t("settings", { defaultValue: "Settings" })}
                  >
                    <InfoSettingsIcon />
                  </button>
                </>
              )}
            </div>
          </div>

          <div
            ref={mobileChrome?.swipeViewportRef}
            className="sb-reader-swipe-viewport"
          >
            <div
              ref={mobileChrome?.swipeTrackRef}
              className="sb-reader-swipe-track"
            >
              <div
                className="sb-reader-swipe-panel sb-reader-swipe-panel-side"
                aria-hidden="true"
              >
                {mobileChrome?.prevChapterPreview &&
                  renderMobileChapterTitle(
                    mobileChrome.prevChapterPreview.book.name,
                    mobileChrome.prevChapterPreview.chapter.number
                  )}
                <div className="sb-chapter-content sb-chapter-content-note-gutter">
                  {renderStaticChapterContent(
                    mobileChrome?.prevChapterPreview ?? null,
                    scriptureElements
                  )}
                </div>
              </div>
              <div
                ref={mobileChrome?.currentScrollerRefCallback}
                className="sb-reader-swipe-panel sb-reader-swipe-panel-current"
              >
                {renderMainContent()}
                {extraContent}
              </div>
              <div
                className="sb-reader-swipe-panel sb-reader-swipe-panel-side"
                aria-hidden="true"
              >
                {mobileChrome?.nextChapterPreview &&
                  renderMobileChapterTitle(
                    mobileChrome.nextChapterPreview.book.name,
                    mobileChrome.nextChapterPreview.chapter.number
                  )}
                <div className="sb-chapter-content sb-chapter-content-note-gutter">
                  {renderStaticChapterContent(
                    mobileChrome?.nextChapterPreview ?? null,
                    scriptureElements
                  )}
                </div>
              </div>
            </div>
          </div>

          {mobileChrome?.showMobileSettings && (
            <MobileSettingsSheet
              state={state}
              onClose={() => mobileChrome.onCloseMobileSettings()}
              onOpenAllSettings={() => mobileChrome.onOpenAllSettings()}
            />
          )}
        </Fragment>
      ) : (
        <Fragment key="desktop">
          <div className="sb-bible-reader-header">
            <div className="sb-bible-reader-heading">
              <h2
                {...flingSafeTapHandlers(openBookSelector)}
                className="sb-bible-reader-title"
              >
                <span className="sb-bible-reader-book">
                  {currentBookName.value ?? bookId.value ?? "Select a book"}
                </span>
                <span className="sb-bible-reader-title-sep" aria-hidden="true">
                  {" "}
                </span>
                <span className="sb-bible-reader-chapter">
                  {chapterNumber.value}
                </span>
              </h2>
              <button
                type="button"
                className="sb-bible-reader-translation"
                aria-label={changeTranslationLabel}
                onClick={() => {
                  void openTranslationSelector();
                }}
              >
                {translationLabel}
              </button>
            </div>
            {state && (
              <div className="sb-bible-reader-actions">
                {/* Chapter actions lead, extension quick tools follow — the
                    same order as the mobile header cluster below, so Save
                    doesn't swap sides with Share between breakpoints. */}
                {!state.playlists.playing.value && (
                  <>
                    <ReaderSaveButton
                      state={state}
                      translationId={translationId.value}
                      bookId={bookId.value}
                      chapterNumber={chapterNumber.value}
                    />
                    <ReaderBookmarkButton
                      state={state}
                      translationId={translationId.value}
                      bookId={bookId.value}
                      chapterNumber={chapterNumber.value}
                    />
                  </>
                )}
                <QuickToolbar
                  toolsManager={state.tools}
                  readingState={readingState}
                  playlists={state.playlists}
                  annotations={state.annotations}
                  features={state.features}
                  settings={state.settings}
                  sharedSession={sharedSession ?? null}
                  readingPlans={state.readingPlans}
                  toast={state.app.toast}
                  modals={state.modals}
                  app={state.app}
                  className="sb-quick-toolbar-reader"
                />
              </div>
            )}
          </div>
          <div
            className={`sb-bible-reader-content${
              readingState.discoverContentPanelInline.value === false
                ? " sb-bible-reader-content--discover-below"
                : ""
            }`}
          >
            <div className="sb-bible-reader-main-content">
              {renderMainContent()}
            </div>
            {extraContent}
          </div>
        </Fragment>
      )}

      {scriptureElements.showFootnotes && selectedFootnote.value !== null && (
        <div className="sb-footnote-modal-overlay" {...footnoteOverlayDismiss}>
          <div
            className="sb-footnote-modal"
            onClick={(event: MouseEvent) => {
              event.stopPropagation();
            }}
          >
            <div className="sb-footnote-modal-header">
              <h3 className="sb-footnote-modal-title">
                {selectedFootnote.value.chapter.book.name}{" "}
                {selectedFootnote.value.chapter.chapter.number}
                {selectedFootnote.value.verse
                  ? ":" + selectedFootnote.value.verse.number
                  : ""}
              </h3>
              <button
                className="sb-footnote-modal-close"
                aria-label={t("close-footnote", {
                  defaultValue: "Close footnote",
                })}
                onClick={() => {
                  selectFootnote(null);
                }}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="sb-footnote-modal-content">
              <VerseReferenceText
                text={selectedFootnote.value.note.text}
                books={translationBooks.value?.books}
                onReferenceClick={(ref) => {
                  selectFootnote(null);
                  void state?.app.openVerseReference(ref);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
